import Axios from "axios";

declare module "axios" {
  interface AxiosRequestConfig {
    systemOneApiConfig?: SystemOneApiConfig;
  }
}

interface SystemOneApiConfig {
  toastOnError: boolean;
}

const endpoint = <F extends (...args: any[]) => any, U extends (...args: any[]) => string>(
  fn: F,
  absoluteUrl: U,
  requestSizeLimit = 3145728
) => {
  return Object.assign(fn, { absoluteUrl, requestSizeLimit });
}

const api = Axios.create({ withCredentials: true });

const createApiFunctions = (baseURL: string, acceptLanguageValue?: string) => {
  api.defaults.baseURL = baseURL;
  if (acceptLanguageValue != null) {
      api.defaults.headers.common["Accept-Language"] = acceptLanguageValue;
  }

  const apiFunctions = {
    Account: {
      LoginRedirect: endpoint(
        (returnUrl: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/account/login`, { params: { returnUrl }, systemOneApiConfig }),
        (returnUrl: string) => `${baseURL}/account/login?returnUrl=${returnUrl}`,
        3145728
      ),
      Me: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserSmall>>(`/account/me`, { systemOneApiConfig }),
        () => `${baseURL}/account/me`,
        3145728
      ),
      ResetMyPassword: endpoint(
        (dto: server.dto.ResetMyPasswordInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ResetMyPasswordOutcome>>(`/account/password/ResetMyPassword`, dto, { systemOneApiConfig }),
        () => `${baseURL}/account/password/ResetMyPassword`,
        3145728
      ),
      ChangeMyPassword: endpoint(
        (dto: server.dto.ChangePasswordInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/account/password/changemypassword`, dto, { systemOneApiConfig }),
        () => `${baseURL}/account/password/changemypassword`,
        3145728
      ),
      ValidateMyPassword: endpoint(
        (dto: server.dto.ValidatePasswordInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<boolean>>(`/account/password/validatemypassword`, dto, { systemOneApiConfig }),
        () => `${baseURL}/account/password/validatemypassword`,
        3145728
      ),
      ValidatePasswordFormatting: endpoint(
        (dto: server.dto.ValidatePasswordInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/account/password/validateformatting`, dto, { systemOneApiConfig }),
        () => `${baseURL}/account/password/validateformatting`,
        3145728
      ),
      ChangeMyName: endpoint(
        (dto: server.dto.UpdateUserPersonalNameInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/account/my/changename`, dto, { systemOneApiConfig }),
        () => `${baseURL}/account/my/changename`,
        3145728
      ),
      ChangeSecretQuestion: endpoint(
        (dto: server.dto.UpdateSecretQuestionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/account/changesecretquestion`, dto, { systemOneApiConfig }),
        () => `${baseURL}/account/changesecretquestion`,
        3145728
      ),
      GetCurrentSecretQuestion: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<number>>(`/account/currentsecretquestion`, { systemOneApiConfig }),
        () => `${baseURL}/account/currentsecretquestion`,
        3145728
      ),
      GetCurrentSecretAnswer: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/account/currentsecretanswer`, { systemOneApiConfig }),
        () => `${baseURL}/account/currentsecretanswer`,
        3145728
      ),
      GetSiteData: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.SiteData>>(`/account/siteData`, { systemOneApiConfig }),
        () => `${baseURL}/account/siteData`,
        3145728
      )
    },
    AdminOne: {
      IndexStorageAccount: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/adminone/IndexStorageAccount`, { systemOneApiConfig }),
        () => `${baseURL}/adminone/IndexStorageAccount`,
        3145728
      ),
      CreateFlightParserTest: endpoint(
        (passengerFlightId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/adminone/CreateFlightParserTest`, { params: { passengerFlightId }, systemOneApiConfig }),
        (passengerFlightId: number) => `${baseURL}/adminone/CreateFlightParserTest?passengerFlightId=${passengerFlightId}`,
        3145728
      ),
      GenerateAzCopyScript: endpoint(
        (sourceSasToken: string, destinationSasToken: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string[]>>(`/adminone/GenerateAzCopyScript`, { params: { sourceSasToken, destinationSasToken }, systemOneApiConfig }),
        (sourceSasToken: string, destinationSasToken: string) => `${baseURL}/adminone/GenerateAzCopyScript?sourceSasToken=${sourceSasToken}&destinationSasToken=${destinationSasToken}`,
        3145728
      ),
      GetRedisPing: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/adminone/redis/ping`, { systemOneApiConfig }),
        () => `${baseURL}/adminone/redis/ping`,
        3145728
      ),
      SubscriptionInvoicePerYearDivision: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/adminone/SubscriptionInvoicePerYearDivision`, { systemOneApiConfig }),
        () => `${baseURL}/adminone/SubscriptionInvoicePerYearDivision`,
        3145728
      ),
      SubscriptionChurnAndGrowth: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.SubscriptionChurnAndGrowth[]>>(`/adminone/SubscriptionChurnAndGrowth`, { systemOneApiConfig }),
        () => `${baseURL}/adminone/SubscriptionChurnAndGrowth`,
        3145728
      ),
      SubscriptionMetric: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.SubscriptionMetric[]>>(`/adminone/SubscriptionMetric`, { systemOneApiConfig }),
        () => `${baseURL}/adminone/SubscriptionMetric`,
        3145728
      ),
      DownloadResX: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/adminone/downloadResX`, { systemOneApiConfig }),
        () => `${baseURL}/adminone/downloadResX`,
        3145728
      ),
      ListReprimands: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.CheatingReprimand[]>>(`/adminone/Reprimands`, { systemOneApiConfig }),
        () => `${baseURL}/adminone/Reprimands`,
        3145728
      ),
      CreateShowToDateRecords: endpoint(
        (truncateExisting: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/adminone/CreateShowToDateRecords`, undefined, { params: { truncateExisting }, systemOneApiConfig }),
        (truncateExisting: boolean) => `${baseURL}/adminone/CreateShowToDateRecords?truncateExisting=${truncateExisting}`,
        3145728
      )
    },
    AdvancingProvide: {
      AcceptInvitation: endpoint(
        (secret: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/advancingprovide/${secret}/acceptInvitation`, { systemOneApiConfig }),
        (secret: string) => `${baseURL}/advancingprovide/${secret}/acceptInvitation`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (secret: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.AdvancingSessionSidePanelData>>(`/advancingprovide/${secret}/SidePanel`, { systemOneApiConfig }),
        (secret: string) => `${baseURL}/advancingprovide/${secret}/SidePanel`,
        3145728
      ),
      ProvideFormRow: endpoint(
        (dto: server.dto.ProvideAdvancingFormItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/advancingprovide/provideFormRow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingprovide/provideFormRow`,
        3145728
      )
    },
    AdvancingSession: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedAdvancingSessionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.AdvancingSessionForListForInternalUser[], server.dto.ListAdvancingFilters>>(`/advancingsession/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingsession/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (advancingSessionId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.AdvancingSessionSidePanelData>>(`/advancingsession/${advancingSessionId}/SidePanel`, { systemOneApiConfig }),
        (advancingSessionId: number) => `${baseURL}/advancingsession/${advancingSessionId}/SidePanel`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateAdvancingSessionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/advancingsession/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingsession/create`,
        3145728
      ),
      Delete: endpoint(
        (advancingSessionId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/advancingsession/${advancingSessionId}`, { systemOneApiConfig }),
        (advancingSessionId: number) => `${baseURL}/advancingsession/${advancingSessionId}`,
        3145728
      ),
      ApproveFormRow: endpoint(
        (instruction: server.dto.ApproveAdvancingFormRowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/advancingsession/formrow/approveFormRow`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/advancingsession/formrow/approveFormRow`,
        3145728
      ),
      DeclineFormRow: endpoint(
        (dto: server.dto.DeclineAdvancingFormRowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/advancingsession/formrow/declineFormRow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingsession/formrow/declineFormRow`,
        3145728
      ),
      RemoveFormRow: endpoint(
        (dto: server.dto.RemoveAdvancingFormRowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/advancingsession/formrow/removeFormRow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingsession/formrow/removeFormRow`,
        3145728
      ),
      PaginatedMessage: endpoint(
        (dto: server.dto.PaginatedAdvancingMessageInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.AdvancingMessage[]>>(`/advancingsession/messages/paginatedMessages`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingsession/messages/paginatedMessages`,
        3145728
      ),
      SendMessage: endpoint(
        (dto: server.dto.SendAdvancingMessageInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/advancingsession/messages/send`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingsession/messages/send`,
        3145728
      ),
      MessageSeen: endpoint(
        (advancingMessageId: number, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/advancingsession/messages/${advancingMessageId}/seen`, undefined, { systemOneApiConfig }),
        (advancingMessageId: number) => `${baseURL}/advancingsession/messages/${advancingMessageId}/seen`,
        3145728
      ),
      AddAdvancingParticipant: endpoint(
        (dto: server.dto.AddParticipantToSessionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/advancingsession/participants/addAdvancingParticipant`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingsession/participants/addAdvancingParticipant`,
        3145728
      ),
      DeleteParticipant: endpoint(
        (advancingParticipantId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/advancingsession/participants/${advancingParticipantId}/deleteParticipant`, undefined, { systemOneApiConfig }),
        (advancingParticipantId: number) => `${baseURL}/advancingsession/participants/${advancingParticipantId}/deleteParticipant`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedAdvancingActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.AdvancingSessionActivity[]>>(`/advancingsession/ListActivity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingsession/ListActivity`,
        3145728
      )
    },
    AdvancingTemplate: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedAdvancingTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.AdvancingTemplate[], server.dto.ListAdvancingTemplateFilters>>(`/advancingtemplate/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingtemplate/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (advancingTemplateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.AdvancingTemplateSidePanelData>>(`/advancingtemplate/${advancingTemplateId}/SidePanel`, { systemOneApiConfig }),
        (advancingTemplateId: number) => `${baseURL}/advancingtemplate/${advancingTemplateId}/SidePanel`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateAdvancingTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/advancingtemplate/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingtemplate/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateAdvancingTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/advancingtemplate/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingtemplate/update`,
        3145728
      ),
      DeleteTemplate: endpoint(
        (dto: server.dto.DeleteAdvancingTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/advancingtemplate/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingtemplate/delete`,
        3145728
      )
    },
    AdvancingUpload: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedAdvancingUploadInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.AdvancingUpload[]>>(`/advancingupload/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingupload/list`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateAdvancingUploadInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/advancingupload/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingupload/create`,
        209715200
      ),
      DeleteFile: endpoint(
        (dto: server.dto.DeleteAdvancingFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/advancingupload/deleteFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/advancingupload/deleteFile`,
        3145728
      )
    },
    AgeRestriction: {
      List: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.AgeRestriction[]>>(`/agerestriction/list`, { systemOneApiConfig }),
        () => `${baseURL}/agerestriction/list`,
        3145728
      )
    },
    Airline: {
      List: endpoint(
        (dto: server.dto.ListAirlineInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.Airline[]>>(`/airline/autocomplete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/airline/autocomplete`,
        3145728
      )
    },
    Airport: {
      GetAirport: endpoint(
        (airportId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.Airport>>(`/airport/${airportId}`, { systemOneApiConfig }),
        (airportId: number) => `${baseURL}/airport/${airportId}`,
        3145728
      ),
      Paginated: endpoint(
        (dto: server.dto.PaginatedAirportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.Airport[], server.dto.ListAirportFilters>>(`/airport/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/airport/list`,
        3145728
      )
    },
    ArticleCategory: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedArticleCategoryInstruction, section: server.dto.PromoSiteSections, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ArticleCategory[], server.dto.ListArticleCategoryFilters>>(`/articlecategory/${section}/list`, dto, { systemOneApiConfig }),
        (section: server.dto.PromoSiteSections) => `${baseURL}/articlecategory/${section}/list`,
        3145728
      ),
      Create: endpoint(
        (section: server.dto.PromoSiteSections, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapper<server.dto.CreateArticleCategoryOutcome>>(`/articlecategory/create`, undefined, { params: { section }, systemOneApiConfig }),
        (section: server.dto.PromoSiteSections) => `${baseURL}/articlecategory/create?section=${section}`,
        3145728
      ),
      Save: endpoint(
        (dto: server.dto.SaveArticleCategoryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/articlecategory/save`, dto, { systemOneApiConfig }),
        () => `${baseURL}/articlecategory/save`,
        3145728
      ),
      Delete: endpoint(
        (articleCategoryId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/articlecategory/${articleCategoryId}/delete`, { systemOneApiConfig }),
        (articleCategoryId: number) => `${baseURL}/articlecategory/${articleCategoryId}/delete`,
        3145728
      ),
      DeleteTranslation: endpoint(
        (articleCategoryId: number, language: server.dto.Languages, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/articlecategory/${articleCategoryId}/${language}/delete`, { systemOneApiConfig }),
        (articleCategoryId: number, language: server.dto.Languages) => `${baseURL}/articlecategory/${articleCategoryId}/${language}/delete`,
        3145728
      )
    },
    Article: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedArticleInstruction, section: server.dto.PromoSiteSections, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.Article[], server.dto.ListArticleFilters>>(`/article/${section}/list`, dto, { systemOneApiConfig }),
        (section: server.dto.PromoSiteSections) => `${baseURL}/article/${section}/list`,
        3145728
      ),
      SupportByTopic: endpoint(
        (topic: server.dto.ArticleTopics, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ArticleTranslation[]>>(`/article/SupportByTopic`, undefined, { params: { topic }, systemOneApiConfig }),
        (topic: server.dto.ArticleTopics) => `${baseURL}/article/SupportByTopic?topic=${topic}`,
        3145728
      ),
      GetArticle: endpoint(
        (articleId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.Article>>(`/article/${articleId}`, { systemOneApiConfig }),
        (articleId: number) => `${baseURL}/article/${articleId}`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateArticleInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/article/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/article/create`,
        3145728
      ),
      Save: endpoint(
        (dto: server.dto.SaveArticleTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/article/save`, dto, { systemOneApiConfig }),
        () => `${baseURL}/article/save`,
        3145728
      ),
      SetListImage: endpoint(
        (articleId: number, listImageId: string, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/article/${articleId}/list/image`, undefined, { params: { listImageId }, systemOneApiConfig }),
        (articleId: number, listImageId: string) => `${baseURL}/article/${articleId}/list/image?listImageId=${listImageId}`,
        3145728
      ),
      SetHeaderImage: endpoint(
        (articleId: number, headerImageId: string, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/article/${articleId}/header/image`, undefined, { params: { headerImageId }, systemOneApiConfig }),
        (articleId: number, headerImageId: string) => `${baseURL}/article/${articleId}/header/image?headerImageId=${headerImageId}`,
        3145728
      ),
      SetHeaderBackgroundHexColor: endpoint(
        (articleId: number, hexColor: string, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/article/${articleId}/header/hexColor`, undefined, { params: { hexColor }, systemOneApiConfig }),
        (articleId: number, hexColor: string) => `${baseURL}/article/${articleId}/header/hexColor?hexColor=${hexColor}`,
        3145728
      ),
      SoftDelete: endpoint(
        (articleId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/article/${articleId}`, { systemOneApiConfig }),
        (articleId: number) => `${baseURL}/article/${articleId}`,
        3145728
      ),
      DeleteTranslation: endpoint(
        (dto: server.dto.DeleteArticleTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/article/translation/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/article/translation/delete`,
        3145728
      ),
      UploadArticleImage: endpoint(
        (instruction: server.dto.UploadArticleImageInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/article/imageupload`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/article/imageupload`,
        10485760
      )
    },
    Artist: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedArtistInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ArtistForList[], server.dto.ListArtistFilters>>(`/artist/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/artist/list`,
        3145728
      ),
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedArtistInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ArtistSmall[], server.dto.ListArtistFilters>>(`/artist/listSmall`, dto, { systemOneApiConfig }),
        () => `${baseURL}/artist/listSmall`,
        3145728
      ),
      ListUsersWithAccess: endpoint(
        (artistId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserThatCanAccessArtist[]>>(`/artist/${artistId}/UsersWithAccess`, { systemOneApiConfig }),
        (artistId: number) => `${baseURL}/artist/${artistId}/UsersWithAccess`,
        3145728
      )
    },
    ArtistRider: {
      Delete: endpoint(
        (artistRiderId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/artistrider/${artistRiderId}`, { systemOneApiConfig }),
        (artistRiderId: number) => `${baseURL}/artistrider/${artistRiderId}`,
        3145728
      ),
      DownloadZip: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/artistrider/download/all`, { systemOneApiConfig }),
        () => `${baseURL}/artistrider/download/all`,
        3145728
      ),
      Paginated: endpoint(
        (dto: server.dto.PaginatedArtistRiderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ArtistRider[], server.dto.ListArtistRiderFilters>>(`/artistrider/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/artistrider/list`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateArtistRiderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/artistrider/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/artistrider/create`,
        10485760
      ),
      Update: endpoint(
        (dto: server.dto.UpdateArtistRiderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/artistrider/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/artistrider/update`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ArtistRiderActivity[]>>(`/artistrider/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/artistrider/activity`,
        3145728
      ),
      DownloadFile: endpoint(
        (dto: server.dto.DownloadTemplateFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/artistrider/DownloadFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/artistrider/DownloadFile`,
        3145728
      ),
      DownloadFileFromActivity: endpoint(
        (dto: server.dto.DownloadTemplateFileFromActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/artistrider/DownloadFileFromActivity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/artistrider/DownloadFileFromActivity`,
        3145728
      ),
      UploadFile: endpoint(
        (dto: server.dto.UploadTemplateFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/artistrider/UploadFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/artistrider/UploadFile`,
        10485760
      ),
      RestoreFileFromActivity: endpoint(
        (activityId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/artistrider/RestoreFileFromActivity`, undefined, { params: { activityId }, systemOneApiConfig }),
        (activityId: number) => `${baseURL}/artistrider/RestoreFileFromActivity?activityId=${activityId}`,
        3145728
      )
    },
    Authentication: {
      SignInWithCredentials: endpoint(
        (credentials: server.dto.SignInCredentials, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/authentication/signin`, credentials, { systemOneApiConfig }),
        () => `${baseURL}/authentication/signin`,
        3145728
      ),
      SignInWithCredentialsForBearerToken: endpoint(
        (credentials: server.dto.SignInCredentials, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BearerSession>>(`/authentication/signin/bearer`, credentials, { systemOneApiConfig }),
        () => `${baseURL}/authentication/signin/bearer`,
        3145728
      ),
      RenewBearerToken: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BearerSession>>(`/authentication/signin/bearer/renew`, undefined, { systemOneApiConfig }),
        () => `${baseURL}/authentication/signin/bearer/renew`,
        3145728
      ),
      SignInWithGoogleForBearerToken: endpoint(
        (dto: server.dto.SignInWithGoogleForBearerTokenInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BearerExternalSignIn>>(`/authentication/signin/bearer/google`, dto, { systemOneApiConfig }),
        () => `${baseURL}/authentication/signin/bearer/google`,
        3145728
      ),
      SignInWithAppleForBearerToken: endpoint(
        (dto: server.dto.SignInWithAppleForBearerTokenInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BearerExternalSignIn>>(`/authentication/signin/bearer/apple`, dto, { systemOneApiConfig }),
        () => `${baseURL}/authentication/signin/bearer/apple`,
        3145728
      ),
      SignOutBearerToken: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/authentication/signout/bearer`, undefined, { systemOneApiConfig }),
        () => `${baseURL}/authentication/signout/bearer`,
        3145728
      ),
      ImpersonateForBearerToken: endpoint(
        (instruction: server.dto.ImpersonateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BearerSession>>(`/authentication/impersonate/bearer`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/authentication/impersonate/bearer`,
        3145728
      ),
      Signout: endpoint(
        (returnUrl: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/authentication/signout`, { params: { returnUrl }, systemOneApiConfig }),
        (returnUrl: string) => `${baseURL}/authentication/signout?returnUrl=${returnUrl}`,
        3145728
      ),
      Impersonate: endpoint(
        (instruction: server.dto.ImpersonateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/authentication/impersonate`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/authentication/impersonate`,
        3145728
      )
    },
    Availability: {
      GetAvailability: endpoint(
        (dto: server.dto.ListAvailableDatesInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.AvailableDatesPerArtist[]>>(`/availability`, dto, { systemOneApiConfig }),
        () => `${baseURL}/availability`,
        3145728
      )
    },
    BankAccount: {
      DeleteBankAccount: endpoint(
        (bankAccountId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/bankaccount/${bankAccountId}/delete`, { systemOneApiConfig }),
        (bankAccountId: number) => `${baseURL}/bankaccount/${bankAccountId}/delete`,
        3145728
      ),
      UpdateBankAccount: endpoint(
        (dto: server.dto.UpdateBankAccountInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bankaccount/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bankaccount/update`,
        3145728
      ),
      CreateBankAccount: endpoint(
        (dto: server.dto.CreateBankAccountInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/bankaccount/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bankaccount/create`,
        3145728
      ),
      GetBankAccount: endpoint(
        (bankAccountId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.BankAccount>>(`/bankaccount/${bankAccountId}`, { systemOneApiConfig }),
        (bankAccountId: number) => `${baseURL}/bankaccount/${bankAccountId}`,
        3145728
      ),
      ListBankAccount: endpoint(
        (dto: server.dto.PaginatedBankAccountInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.BankAccount[], server.dto.ListBankAccountFilters>>(`/bankaccount/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bankaccount/list`,
        3145728
      ),
      ListBankAccountSmall: endpoint(
        (dto: server.dto.PaginatedBankAccountInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BankAccountSmall[]>>(`/bankaccount/listSmall`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bankaccount/listSmall`,
        3145728
      ),
      PaginatedLinkedBookkeepings: endpoint(
        (dto: server.dto.PaginatedLinkedBookkeepingsToBankAccountInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.BookkeepingSmall[], server.dto.ListLinkedBookkeepingFilters>>(`/bankaccount/paginatedLinkedBookkeepings`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bankaccount/paginatedLinkedBookkeepings`,
        3145728
      ),
      LinkBookkeepings: endpoint(
        (dto: server.dto.LinkUnlinkBookkeepingToBankAccountInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bankaccount/linkBookkeepings`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bankaccount/linkBookkeepings`,
        3145728
      ),
      UnlinkBookkeepings: endpoint(
        (dto: server.dto.LinkUnlinkBookkeepingToBankAccountInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bankaccount/unlinkBookkeepings`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bankaccount/unlinkBookkeepings`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BankAccountActivity[]>>(`/bankaccount/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bankaccount/activity`,
        3145728
      )
    },
    Bookkeeping: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedBookkeepingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.BookkeepingForList[], server.dto.ListBookkeepingFilters>>(`/bookkeeping/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/list`,
        3145728
      ),
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedBookkeepingInstruction, securityScope: server.dto.BookkeepingSecurityScopes, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.BookkeepingSmall[], server.dto.ListBookkeepingFilters>>(`/bookkeeping/listSmall/${securityScope}`, dto, { systemOneApiConfig }),
        (securityScope: server.dto.BookkeepingSecurityScopes) => `${baseURL}/bookkeeping/listSmall/${securityScope}`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (bookkeepingId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.BookkeepingSidePanelData>>(`/bookkeeping/${bookkeepingId}/SidePanel`, { systemOneApiConfig }),
        (bookkeepingId: number) => `${baseURL}/bookkeeping/${bookkeepingId}/SidePanel`,
        3145728
      ),
      GetExchangeRate: endpoint(
        (dto: server.dto.GetExchangeRateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExchangeRate>>(`/bookkeeping/getExchangeRate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/getExchangeRate`,
        3145728
      ),
      GetLogo: endpoint(
        (bookkeepingId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.BookkeepingLogoFile>>(`/bookkeeping/${bookkeepingId}/logo`, { systemOneApiConfig }),
        (bookkeepingId: number) => `${baseURL}/bookkeeping/${bookkeepingId}/logo`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BookkeepingActivity[]>>(`/bookkeeping/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/activity`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateBookkeepingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/bookkeeping/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/create`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteBookkeepingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/delete`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateBookkeepingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/update`,
        3145728
      ),
      UpdateUserPermission: endpoint(
        (dto: server.dto.UpdateUserToClientToBookkeepingPermssionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/updateUserPermission`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/updateUserPermission`,
        3145728
      ),
      CodeExists: endpoint(
        (dto: server.dto.DoesBookkeepingCodeExistsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<boolean>>(`/bookkeeping/codeexists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/codeexists`,
        3145728
      ),
      SetLogo: endpoint(
        (instruction: server.dto.SetBookkeepingLogoInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/bookkeeping/logo/set`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/logo/set`,
        10485760
      ),
      SetEmail: endpoint(
        (dto: server.dto.SetBookkeepingEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/setemail`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/setemail`,
        3145728
      ),
      RemoveEmail: endpoint(
        (dto: server.dto.RemoveBookkeepingEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/removeemail`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/removeemail`,
        3145728
      ),
      ConfigureBookkeepingForAdminOne: endpoint(
        (dto: server.dto.ConfigureBookkeepingForAdminOneInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/ConfigureBookkeepingForAdminOne`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/ConfigureBookkeepingForAdminOne`,
        3145728
      ),
      PaginatedLinkedUserToClients: endpoint(
        (dto: server.dto.PaginatedUserToClientToBookkeepingLinkedUserToClientsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.UserToClientSmall[], server.dto.ListLinkedUserToClientFilters>>(`/bookkeeping/paginatedLinkedUserToClients`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/paginatedLinkedUserToClients`,
        3145728
      ),
      LinkUserToClients: endpoint(
        (dto: server.dto.UserToClientToBookkeepingLinkUnlinkUserToClientsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/linkUserToClients`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/linkUserToClients`,
        3145728
      ),
      UnlinkUserToClients: endpoint(
        (dto: server.dto.UserToClientToBookkeepingLinkUnlinkUserToClientsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/unlinkUserToClients`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/unlinkUserToClients`,
        3145728
      ),
      LinkAllForBookkeeping: endpoint(
        (dto: server.dto.UserToClientToBookkeepingLinkUnlinkAllForBookkeepingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/linkAllForBookkeeping`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/linkAllForBookkeeping`,
        3145728
      ),
      UnlinkAllForBookkeeping: endpoint(
        (dto: server.dto.UserToClientToBookkeepingLinkUnlinkAllForBookkeepingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/unlinkAllForBookkeeping`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/unlinkAllForBookkeeping`,
        3145728
      ),
      PaginatedLinkedWordTemplates: endpoint(
        (dto: server.dto.PaginatedWordTemplateToBookkeepingLinkedWordTemplatesInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.WordTemplate[], server.dto.ListLinkedWordTemplateFilters>>(`/bookkeeping/paginatedLinkedWordTemplates`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/paginatedLinkedWordTemplates`,
        3145728
      ),
      LinkWordTemplates: endpoint(
        (dto: server.dto.WordTemplateToBookkeepingLinkUnlinkWordTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/linkWordTemplates`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/linkWordTemplates`,
        3145728
      ),
      UnlinkWordTemplates: endpoint(
        (dto: server.dto.WordTemplateToBookkeepingLinkUnlinkWordTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/unlinkWordTemplates`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/unlinkWordTemplates`,
        3145728
      ),
      LinkAllTemplatesForBookkeeping: endpoint(
        (dto: server.dto.WordTemplateToBookkeepingLinkUnlinkAllForBookkeepingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/linkAllTemplatesForBookkeeping`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/linkAllTemplatesForBookkeeping`,
        3145728
      ),
      UnlinkAllTemplatesForBookkeeping: endpoint(
        (dto: server.dto.WordTemplateToBookkeepingLinkUnlinkAllForBookkeepingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/bookkeeping/unlinkAllTemplatesForBookkeeping`, dto, { systemOneApiConfig }),
        () => `${baseURL}/bookkeeping/unlinkAllTemplatesForBookkeeping`,
        3145728
      )
    },
    Brand: {
      DeleteBrand: endpoint(
        (brandId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/brand/${brandId}/delete`, { systemOneApiConfig }),
        (brandId: number) => `${baseURL}/brand/${brandId}/delete`,
        3145728
      ),
      CreateBrand: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/brand/create`, undefined, { systemOneApiConfig }),
        () => `${baseURL}/brand/create`,
        3145728
      ),
      GetBrand: endpoint(
        (brandId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.Brand>>(`/brand/${brandId}`, { systemOneApiConfig }),
        (brandId: number) => `${baseURL}/brand/${brandId}`,
        3145728
      ),
      List: endpoint(
        (dto: server.dto.PaginatedBrandInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.Brand[], server.dto.ListBrandFilters>>(`/brand/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/brand/list`,
        3145728
      ),
      ListBrandSmall: endpoint(
        (dto: server.dto.PaginatedBrandInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BrandSmall[]>>(`/brand/list/small`, dto, { systemOneApiConfig }),
        () => `${baseURL}/brand/list/small`,
        3145728
      ),
      SetLogo: endpoint(
        (instruction: server.dto.SetBrandLogoInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/brand/logo/set`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/brand/logo/set`,
        10485760
      ),
      SetIcon: endpoint(
        (instruction: server.dto.SetBrandIconInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/brand/icon/set`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/brand/icon/set`,
        10485760
      ),
      SetName: endpoint(
        (dto: server.dto.SetBrandNameInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/brand/name/set`, dto, { systemOneApiConfig }),
        () => `${baseURL}/brand/name/set`,
        3145728
      ),
      SetFriendlyId: endpoint(
        (dto: server.dto.SetBrandFriendlyIdInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/brand/friendlyid/set`, dto, { systemOneApiConfig }),
        () => `${baseURL}/brand/friendlyid/set`,
        3145728
      ),
      SetWebsite: endpoint(
        (dto: server.dto.SetBrandWebsiteInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/brand/website/set`, dto, { systemOneApiConfig }),
        () => `${baseURL}/brand/website/set`,
        3145728
      ),
      SetAsDefaultForClient: endpoint(
        (brandId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<void>(`/brand/${brandId}/default`, undefined, { systemOneApiConfig }),
        (brandId: number) => `${baseURL}/brand/${brandId}/default`,
        3145728
      ),
      SetESignBackgroundFooterColor: endpoint(
        (dto: server.dto.SetBrandESignFooterBackgroundColorInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/brand/eSignBackgroundFooterColor/set`, dto, { systemOneApiConfig }),
        () => `${baseURL}/brand/eSignBackgroundFooterColor/set`,
        3145728
      )
    },
    BusinessCentral: {
      GetCustomer: endpoint(
        (number: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.BusinessCentralCustomer>>(`/businesscentral/GetCustomer`, { params: { number }, systemOneApiConfig }),
        (number: string) => `${baseURL}/businesscentral/GetCustomer?number=${number}`,
        3145728
      ),
      ListProjects: endpoint(
        (dto: server.dto.PaginatedBusinessCentralProjectInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.BusinessCentralProject[], server.dto.ListBusinessCentralProjectFilters>>(`/businesscentral/ListProjects`, dto, { systemOneApiConfig }),
        () => `${baseURL}/businesscentral/ListProjects`,
        3145728
      ),
      CreateProject: endpoint(
        (dto: server.dto.BusinessCentralProjectMutate, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/businesscentral/CreateProject`, dto, { systemOneApiConfig }),
        () => `${baseURL}/businesscentral/CreateProject`,
        3145728
      ),
      UpdateProject: endpoint(
        (dto: server.dto.BusinessCentralProjectMutate, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/businesscentral/UpdateProject`, dto, { systemOneApiConfig }),
        () => `${baseURL}/businesscentral/UpdateProject`,
        3145728
      ),
      SyncBusinessCentralCustomers: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/businesscentral/SyncCustomers`, { systemOneApiConfig }),
        () => `${baseURL}/businesscentral/SyncCustomers`,
        3145728
      ),
      SyncBusinessCentralCostCenters: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/businesscentral/SyncCostCenters`, { systemOneApiConfig }),
        () => `${baseURL}/businesscentral/SyncCostCenters`,
        3145728
      ),
      SyncBusinessCentralProjects: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/businesscentral/SyncProjects`, { systemOneApiConfig }),
        () => `${baseURL}/businesscentral/SyncProjects`,
        3145728
      )
    },
    Checklist: {
      SetESignChecklistItemState: endpoint(
        (dto: server.dto.SetDocumentChecklistItemStateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/checklist/eSign/setState`, dto, { systemOneApiConfig }),
        () => `${baseURL}/checklist/eSign/setState`,
        3145728
      ),
      ListESignChecklistItems: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.DocumentChecklistItem[]>>(`/checklist/eSign/${documentId}/list`, undefined, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/checklist/eSign/${documentId}/list`,
        3145728
      ),
      DeleteESignChecklistItem: endpoint(
        (documentChecklistItemId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/checklist/eSign/deleteItem`, undefined, { params: { documentChecklistItemId }, systemOneApiConfig }),
        (documentChecklistItemId: number) => `${baseURL}/checklist/eSign/deleteItem?documentChecklistItemId=${documentChecklistItemId}`,
        3145728
      ),
      WhoCanReviewESignChecklistItem: endpoint(
        (documentChecklistItemId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.WhoCanReviewDocumentChecklistItem>>(`/checklist/eSign/${documentChecklistItemId}/whoCanReview`, undefined, { systemOneApiConfig }),
        (documentChecklistItemId: number) => `${baseURL}/checklist/eSign/${documentChecklistItemId}/whoCanReview`,
        3145728
      ),
      SetInvoiceV2ChecklistItemState: endpoint(
        (dto: server.dto.SetDocumentChecklistItemStateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/checklist/invoiceV2/setState`, dto, { systemOneApiConfig }),
        () => `${baseURL}/checklist/invoiceV2/setState`,
        3145728
      ),
      ListInvoiceV2ChecklistItems: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.DocumentChecklistItem[]>>(`/checklist/invoiceV2/${invoiceId}/list`, undefined, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/checklist/invoiceV2/${invoiceId}/list`,
        3145728
      ),
      DeleteInvoiceV2ChecklistItem: endpoint(
        (documentChecklistItemId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/checklist/invoiceV2/deleteItem`, undefined, { params: { documentChecklistItemId }, systemOneApiConfig }),
        (documentChecklistItemId: number) => `${baseURL}/checklist/invoiceV2/deleteItem?documentChecklistItemId=${documentChecklistItemId}`,
        3145728
      ),
      WhoCanReviewInvoiceV2ChecklistItem: endpoint(
        (documentChecklistItemId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.WhoCanReviewDocumentChecklistItem>>(`/checklist/invoiceV2/${documentChecklistItemId}/whoCanReview`, undefined, { systemOneApiConfig }),
        (documentChecklistItemId: number) => `${baseURL}/checklist/invoiceV2/${documentChecklistItemId}/whoCanReview`,
        3145728
      )
    },
    ChecklistPreset: {
      DeleteChecklistPresetItem: endpoint(
        (checklistPresetItemId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/checklistpreset/item/${checklistPresetItemId}/delete`, { systemOneApiConfig }),
        (checklistPresetItemId: number) => `${baseURL}/checklistpreset/item/${checklistPresetItemId}/delete`,
        3145728
      ),
      UpdateChecklistPresetItem: endpoint(
        (dto: server.dto.UpdateChecklistPresetItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/checklistpreset/item/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/checklistpreset/item/update`,
        3145728
      ),
      CreateChecklistPresetItem: endpoint(
        (dto: server.dto.CreateChecklistPresetItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/checklistpreset/item/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/checklistpreset/item/create`,
        3145728
      ),
      ListChecklistPresetItem: endpoint(
        (checklistType: server.dto.ChecklistTypes, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ChecklistPresetItem[]>>(`/checklistpreset/item/list`, undefined, { params: { checklistType }, systemOneApiConfig }),
        (checklistType: server.dto.ChecklistTypes) => `${baseURL}/checklistpreset/item/list?checklistType=${checklistType}`,
        3145728
      ),
      UpdateChecklistPresetItemOrder: endpoint(
        (dto: server.dto.UpdateChecklistPresetItemOrderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/checklistpreset/item/sort`, dto, { systemOneApiConfig }),
        () => `${baseURL}/checklistpreset/item/sort`,
        3145728
      )
    },
    ClientConfiguration: {
      Update: endpoint(
        (dto: server.dto.UpdateClientConfigurationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/clientconfiguration/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/clientconfiguration/update`,
        3145728
      )
    },
    Client: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ClientForAdminOneForList[], server.dto.ListClientFilters>>(`/client/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/list`,
        3145728
      ),
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ClientSmall[], server.dto.ListClientFilters>>(`/client/listSmall`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/listSmall`,
        3145728
      ),
      GetClientBillingDetails: endpoint(
        (clientId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ClientBillingDetails>>(`/client/${clientId}/BillingDetails`, { systemOneApiConfig }),
        (clientId: number) => `${baseURL}/client/${clientId}/BillingDetails`,
        3145728
      ),
      UpdateClientAccess: endpoint(
        (dto: server.dto.UpdateClientAccessInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/UpdateClientAccess`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/UpdateClientAccess`,
        3145728
      ),
      ResetClientAccess: endpoint(
        (dto: server.dto.ResetClientAccessInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/ResetClientAccess`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/ResetClientAccess`,
        3145728
      ),
      UpdateClientDiscount: endpoint(
        (dto: server.dto.UpdateClientDiscountInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/UpdateClientDiscount`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/UpdateClientDiscount`,
        3145728
      ),
      SetClientNextSubscription: endpoint(
        (dto: server.dto.SetNextSubscriptionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/SetClientNextSubscription`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/SetClientNextSubscription`,
        3145728
      ),
      UpdateClientCustomerDate: endpoint(
        (dto: server.dto.UpdateClientCustomerDateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/UpdateClientCustomerDate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/UpdateClientCustomerDate`,
        3145728
      ),
      UpdateInvoicingTemplateSetting: endpoint(
        (dto: server.dto.UpdateClientInvoicingTemplateSettingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/UpdateInvoicingTemplateSetting`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/UpdateInvoicingTemplateSetting`,
        3145728
      ),
      UpdateActiveSubscription: endpoint(
        (dto: server.dto.ChangeActiveSubscriptionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/UpdateActiveSubscription`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/UpdateActiveSubscription`,
        3145728
      ),
      UpdateCompany: endpoint(
        (dto: server.dto.UpdateClientCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/UpdateCompany`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/UpdateCompany`,
        3145728
      ),
      UpdateContact: endpoint(
        (dto: server.dto.UpdateClientContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/UpdateContact`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/UpdateContact`,
        3145728
      ),
      EndActiveAndStartNewSubscription: endpoint(
        (dto: server.dto.ChangeActiveSubscriptionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/EndActiveAndStartNewSubscription`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/EndActiveAndStartNewSubscription`,
        3145728
      ),
      UpdatePaymentMethodPerInvoice: endpoint(
        (dto: server.dto.UpdatePaymentMethodPerInvoiceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/UpdatePaymentMethodPerInvoice`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/UpdatePaymentMethodPerInvoice`,
        3145728
      ),
      UpdatePaymentMethodSepaDebit: endpoint(
        (dto: server.dto.UpdatePaymentMethodSepaDebitInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/UpdatePaymentMethodSepaDebit`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/UpdatePaymentMethodSepaDebit`,
        3145728
      ),
      RemoveVatRateFromClient: endpoint(
        (dto: server.dto.RemoveVatRateFromClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/RemoveVatRateFromClient`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/RemoveVatRateFromClient`,
        3145728
      ),
      AddVatRateToClient: endpoint(
        (dto: server.dto.AddVatRateFromClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/AddVatRateToClient`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/AddVatRateToClient`,
        3145728
      ),
      CreateNewClientWithTrial: endpoint(
        (dto: server.dto.CreateClientWithTrialInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapper<number>>(`/client/CreateNewClientWithTrial`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/CreateNewClientWithTrial`,
        3145728
      ),
      CreateNewClientWithPaidSubscription: endpoint(
        (dto: server.dto.CreateClientWithPaidSubscriptionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapper<number>>(`/client/CreateNewClientWithPaidSubscription`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/CreateNewClientWithPaidSubscription`,
        3145728
      ),
      ListClientsWithPaymentMethodProblem: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ClientWithPaymentMethodProblem[]>>(`/client/paymentmethodproblem/list`, { systemOneApiConfig }),
        () => `${baseURL}/client/paymentmethodproblem/list`,
        3145728
      ),
      SetHubSpotDealId: endpoint(
        (dto: server.dto.SetHubSpotDealIdInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/client/SetHubSpotDealId`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/SetHubSpotDealId`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ClientActivity[]>>(`/client/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/client/activity`,
        3145728
      )
    },
    ClientOnboarding: {
      Dashboard: endpoint(
        (dto: server.dto.ListClientOnboardingDashboardInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ClientOnboardingDashboard>>(`/clientonboarding/dashboard`, dto, { systemOneApiConfig }),
        () => `${baseURL}/clientonboarding/dashboard`,
        3145728
      ),
      Remove: endpoint(
        (clientOnboardingId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/clientonboarding/remove`, undefined, { params: { clientOnboardingId }, systemOneApiConfig }),
        (clientOnboardingId: number) => `${baseURL}/clientonboarding/remove?clientOnboardingId=${clientOnboardingId}`,
        3145728
      )
    },
    ClientOnboardingOpen: {
      StartClientOnboarding: endpoint(
        (dto: server.dto.StartClientOnboardingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/clientonboardingopen/start`, dto, { systemOneApiConfig }),
        () => `${baseURL}/clientonboardingopen/start`,
        3145728
      ),
      SetYourDetails: endpoint(
        (dto: server.dto.SetClientOnboardingYourDetailsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/clientonboardingopen/SetYourDetails`, dto, { systemOneApiConfig }),
        () => `${baseURL}/clientonboardingopen/SetYourDetails`,
        3145728
      ),
      SetSubscription: endpoint(
        (dto: server.dto.SetClientOnboardingSubscriptionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/clientonboardingopen/SetSubscription`, dto, { systemOneApiConfig }),
        () => `${baseURL}/clientonboardingopen/SetSubscription`,
        3145728
      ),
      CreateSetupIntent: endpoint(
        (dto: server.dto.CreateClientOnboardingStripeSetupIntentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/clientonboardingopen/stripe/setupintent/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/clientonboardingopen/stripe/setupintent/create`,
        3145728
      ),
      SetLifecycle: endpoint(
        (dto: server.dto.SetClientOnboardingLifeCycleInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/clientonboardingopen/SetLifecycle`, dto, { systemOneApiConfig }),
        () => `${baseURL}/clientonboardingopen/SetLifecycle`,
        3145728
      ),
      GetOnboardingSession: endpoint(
        (secret: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ClientOnboardingSession>>(`/clientonboardingopen/${secret}`, { systemOneApiConfig }),
        (secret: string) => `${baseURL}/clientonboardingopen/${secret}`,
        3145728
      )
    },
    CobraAgency: {
      CreateShow: endpoint(
        (dto: server.dto.CreateCobraAgencyShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.CreateCobraAgencyShowOutcomeV2>(`/cobraagency/createshow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/cobraagency/createshow`,
        3145728
      )
    },
    Company: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.CompanyForList[], server.dto.ListCompanyFilters>>(`/company/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/list`,
        3145728
      ),
      PaginatedFiles: endpoint(
        (dto: server.dto.PaginatedFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.File[]>>(`/company/listFiles`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/listFiles`,
        3145728
      ),
      PaginatedEmails: endpoint(
        (dto: server.dto.PaginatedEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.EmailMessage[]>>(`/company/listEmails`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/listEmails`,
        3145728
      ),
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedCompanyInstruction, type: server.dto.AddressTypes, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.CompanySmall[], server.dto.ListCompanyFilters>>(`/company/listSmall/${type}`, dto, { systemOneApiConfig }),
        (type: server.dto.AddressTypes) => `${baseURL}/company/listSmall/${type}`,
        3145728
      ),
      ListRelatedFiles: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>(`/company/${companyId}/files/related`, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/company/${companyId}/files/related`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.CompanySidePanelData>>(`/company/${companyId}/SidePanel`, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/company/${companyId}/SidePanel`,
        3145728
      ),
      CanReceivePeppol: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.CompanyCanReceivePeppol>>(`/company/${companyId}/CanReceivePeppol`, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/company/${companyId}/CanReceivePeppol`,
        3145728
      ),
      Delete: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/company/${companyId}/delete`, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/company/${companyId}/delete`,
        3145728
      ),
      Restore: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/company/${companyId}/restore`, undefined, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/company/${companyId}/restore`,
        3145728
      ),
      GetProcessedEmailTemplate: endpoint(
        (dto: server.dto.GetProcessedEmailTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ProcessedEmailTemplate>>(`/company/GetProcessedEmailTemplate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/GetProcessedEmailTemplate`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/company/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/company/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/update`,
        3145728
      ),
      Merge: endpoint(
        (dto: server.dto.MergeCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/company/merge`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/merge`,
        3145728
      ),
      AllowMerge: endpoint(
        (dto: server.dto.AllowMergeCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<boolean>>(`/company/allowMerge`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/allowMerge`,
        3145728
      ),
      Validate: endpoint(
        (dto: server.dto.ApproveCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/company/Approve`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/Approve`,
        3145728
      ),
      UpdateTags: endpoint(
        (dto: server.dto.UpdateParentTagsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/company/updateTags`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/updateTags`,
        3145728
      ),
      SetBusinessCentralNumber: endpoint(
        (dto: server.dto.SetBusinessCentralNumberInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/company/update/BusinessCentralNumber`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/update/BusinessCentralNumber`,
        3145728
      ),
      SetHubSpotCompanyId: endpoint(
        (dto: server.dto.SetHubSpotCompanyIdInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/company/update/HubSpotCompanyId`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/update/HubSpotCompanyId`,
        3145728
      ),
      ExportItem: endpoint(
        (companyId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/company/export/item/${companyId}/${exportAction}/${clientFileName}`, { params: { wordTemplateId, preferredCulture }, systemOneApiConfig }),
        (companyId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID) => `${baseURL}/company/export/item/${companyId}/${exportAction}/${clientFileName}?wordTemplateId=${wordTemplateId}&preferredCulture=${preferredCulture}`,
        3145728
      ),
      ExportList: endpoint(
        (fileName: string, page: number, templateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/company/export/list/${fileName}`, { params: { page, templateId }, systemOneApiConfig }),
        (fileName: string, page: number, templateId: number) => `${baseURL}/company/export/list/${fileName}?page=${page}&templateId=${templateId}`,
        3145728
      ),
      ExportEmails: endpoint(
        (fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/company/export/emails/${fileName}`, { systemOneApiConfig }),
        (fileName: string) => `${baseURL}/company/export/emails/${fileName}`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.CompanyActivity[]>>(`/company/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/activity`,
        3145728
      ),
      ExportOptionsForList: endpoint(
        (dto: server.dto.PaginatedCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/company/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/ExportOptionsForList`,
        3145728
      ),
      ExportOptionsForDetail: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/company/ExportOptionsForDetail/${companyId}`, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/company/ExportOptionsForDetail/${companyId}`,
        3145728
      ),
      NameExists: endpoint(
        (dto: server.dto.CompanyNameExistsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<boolean>>(`/company/nameexists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/nameexists`,
        3145728
      ),
      UpdateMarker: endpoint(
        (dto: server.dto.UpdateCompanyGeoCodeOnMapInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/company/marker`, dto, { systemOneApiConfig }),
        () => `${baseURL}/company/marker`,
        3145728
      )
    },
    CompanyToContact: {
      SetLink: endpoint(
        (dto: server.dto.SetCompanyToContactLinkInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/companytocontact/SetLink`, dto, { systemOneApiConfig }),
        () => `${baseURL}/companytocontact/SetLink`,
        3145728
      ),
      DeleteLink: endpoint(
        (dto: server.dto.DeleteCompanyToContactLinkInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/companytocontact/DeleteLink`, dto, { systemOneApiConfig }),
        () => `${baseURL}/companytocontact/DeleteLink`,
        3145728
      ),
      ListCompanyByContact: endpoint(
        (dto: server.dto.ListCompanyByContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.CompanyWithWorkRelation[]>>(`/companytocontact/ListCompanyByContact`, dto, { systemOneApiConfig }),
        () => `${baseURL}/companytocontact/ListCompanyByContact`,
        3145728
      ),
      ListContactByCompany: endpoint(
        (dto: server.dto.ListContactByCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ContactWithWorkRelation[]>>(`/companytocontact/ListContactByCompany`, dto, { systemOneApiConfig }),
        () => `${baseURL}/companytocontact/ListContactByCompany`,
        3145728
      )
    },
    ContactAddressBook: {
      SearchAddressBook: endpoint(
        (dto: server.dto.ListContactAddressBookInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ContactAddressBookEntry[]>>(`/contactaddressbook/SearchAddressBook`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contactaddressbook/SearchAddressBook`,
        3145728
      ),
      RelatedToItem: endpoint(
        (dto: server.dto.ListContactAddressBookToItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ContactAddressBookEntry[]>>(`/contactaddressbook/related`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contactaddressbook/related`,
        3145728
      )
    },
    ContactAlias: {
      ListByContact: endpoint(
        (contactId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ContactAlias[]>>(`/contactalias/listByContact/${contactId}`, { systemOneApiConfig }),
        (contactId: number) => `${baseURL}/contactalias/listByContact/${contactId}`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateContactAliasInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/contactalias/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contactalias/create`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteContactAliasInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contactalias/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contactalias/delete`,
        3145728
      )
    },
    Contact: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ContactForList[], server.dto.ListContactFilters>>(`/contact/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/list`,
        3145728
      ),
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ContactSmallV2[], server.dto.ListContactFilters>>(`/contact/listSmall`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/listSmall`,
        3145728
      ),
      PaginatedFiles: endpoint(
        (dto: server.dto.PaginatedFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.File[]>>(`/contact/listFiles`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/listFiles`,
        3145728
      ),
      PaginatedEmails: endpoint(
        (dto: server.dto.PaginatedEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.EmailMessage[]>>(`/contact/listEmails`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/listEmails`,
        3145728
      ),
      ListRelatedFiles: endpoint(
        (contactId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>(`/contact/${contactId}/files/related`, { systemOneApiConfig }),
        (contactId: number) => `${baseURL}/contact/${contactId}/files/related`,
        3145728
      ),
      Delete: endpoint(
        (contactId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/contact/${contactId}/delete`, { systemOneApiConfig }),
        (contactId: number) => `${baseURL}/contact/${contactId}/delete`,
        3145728
      ),
      Restore: endpoint(
        (contactId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contact/${contactId}/restore`, undefined, { systemOneApiConfig }),
        (contactId: number) => `${baseURL}/contact/${contactId}/restore`,
        3145728
      ),
      GetProcessedEmailTemplate: endpoint(
        (dto: server.dto.GetProcessedEmailTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ProcessedEmailTemplate>>(`/contact/GetProcessedEmailTemplate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/GetProcessedEmailTemplate`,
        3145728
      ),
      Birthdays: endpoint(
        (local: Date, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ContactBirthday[]>>(`/contact/birthdays`, { params: { local }, systemOneApiConfig }),
        (local: Date) => `${baseURL}/contact/birthdays?local=${local}`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (contactId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ContactSidePanelData>>(`/contact/${contactId}/SidePanel`, { systemOneApiConfig }),
        (contactId: number) => `${baseURL}/contact/${contactId}/SidePanel`,
        3145728
      ),
      SetProfileImage: endpoint(
        (instruction: server.dto.SetContactProfileImageInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/contact/profileimage/set`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/contact/profileimage/set`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/contact/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/create`,
        3145728
      ),
      Merge: endpoint(
        (dto: server.dto.MergeContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contact/merge`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/merge`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contact/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/update`,
        3145728
      ),
      UpdateTags: endpoint(
        (dto: server.dto.UpdateParentTagsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contact/updateTags`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/updateTags`,
        3145728
      ),
      Validate: endpoint(
        (dto: server.dto.ApproveContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contact/Approve`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/Approve`,
        3145728
      ),
      ExportItem: endpoint(
        (contactId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/contact/export/item/${contactId}/${exportAction}/${clientFileName}`, { params: { wordTemplateId, preferredCulture }, systemOneApiConfig }),
        (contactId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID) => `${baseURL}/contact/export/item/${contactId}/${exportAction}/${clientFileName}?wordTemplateId=${wordTemplateId}&preferredCulture=${preferredCulture}`,
        3145728
      ),
      ExportList: endpoint(
        (fileName: string, page: number, templateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/contact/export/list/${fileName}`, { params: { page, templateId }, systemOneApiConfig }),
        (fileName: string, page: number, templateId: number) => `${baseURL}/contact/export/list/${fileName}?page=${page}&templateId=${templateId}`,
        3145728
      ),
      ExportEmails: endpoint(
        (fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/contact/export/emails/${fileName}`, { systemOneApiConfig }),
        (fileName: string) => `${baseURL}/contact/export/emails/${fileName}`,
        3145728
      ),
      ExportOptionsForList: endpoint(
        (dto: server.dto.PaginatedContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/contact/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/ExportOptionsForList`,
        3145728
      ),
      ExportOptionsForDetail: endpoint(
        (contactId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/contact/ExportOptionsForDetail/${contactId}`, { systemOneApiConfig }),
        (contactId: number) => `${baseURL}/contact/ExportOptionsForDetail/${contactId}`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ContactActivity[]>>(`/contact/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/activity`,
        3145728
      ),
      NameExists: endpoint(
        (dto: server.dto.ContactNameExistsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<boolean>>(`/contact/nameexists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contact/nameexists`,
        3145728
      ),
      RecentlyUsed: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ContactSmallV2[]>>(`/contact/recentlyused`, { systemOneApiConfig }),
        () => `${baseURL}/contact/recentlyused`,
        3145728
      )
    },
    Contract: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.DocumentForList[], server.dto.ListDocumentFilters>>(`/contract/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.DocumentV1SidePanelData>>(`/contract/${documentId}/SidePanel`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/contract/${documentId}/SidePanel`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateDocumentV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/contract/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/create`,
        3145728
      ),
      CreateDocumentV1FromShow: endpoint(
        (dto: server.dto.CreateDocumentV1FromShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/contract/createFromShow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/createFromShow`,
        3145728
      ),
      Copy: endpoint(
        (dto: server.dto.CopyDocumentV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/contract/copy`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/copy`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateDocumentV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contract/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/update`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contract/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/delete`,
        3145728
      ),
      GetProcessedEmailTemplate: endpoint(
        (dto: server.dto.GetProcessedEmailTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ProcessedEmailTemplate>>(`/contract/GetProcessedEmailTemplate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/GetProcessedEmailTemplate`,
        3145728
      ),
      ExportOptionsForList: endpoint(
        (dto: server.dto.PaginatedDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/contract/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/ExportOptionsForList`,
        3145728
      ),
      LinkShows: endpoint(
        (dto: server.dto.LinkDocumentToShowsInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contract/shows/link`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/shows/link`,
        3145728
      ),
      UnlinkShow: endpoint(
        (dto: server.dto.UnlinkDocumentToShowInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/contract/shows/unlink`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/shows/unlink`,
        3145728
      ),
      PaginatedFiles: endpoint(
        (dto: server.dto.PaginatedFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.File[]>>(`/contract/listFiles`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/listFiles`,
        3145728
      ),
      PaginatedEmails: endpoint(
        (dto: server.dto.PaginatedEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.EmailMessage[]>>(`/contract/listEmails`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/listEmails`,
        3145728
      ),
      ListRelatedFiles: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>(`/contract/${documentId}/files/related`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/contract/${documentId}/files/related`,
        3145728
      ),
      ExportItem: endpoint(
        (documentId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/contract/export/item/${documentId}/${exportAction}/${clientFileName}`, { params: { wordTemplateId, preferredCulture }, systemOneApiConfig }),
        (documentId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID) => `${baseURL}/contract/export/item/${documentId}/${exportAction}/${clientFileName}?wordTemplateId=${wordTemplateId}&preferredCulture=${preferredCulture}`,
        3145728
      ),
      ExportCustomListGeneral: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/contract/export/list/custom/general/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/contract/export/list/custom/general/${fileName}?page=${page}`,
        3145728
      ),
      ExportList: endpoint(
        (fileName: string, page: number, templateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/contract/export/list/${fileName}`, { params: { page, templateId }, systemOneApiConfig }),
        (fileName: string, page: number, templateId: number) => `${baseURL}/contract/export/list/${fileName}?page=${page}&templateId=${templateId}`,
        3145728
      ),
      ExportOptionsForDetail: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/contract/ExportOptionsForDetail/${documentId}`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/contract/ExportOptionsForDetail/${documentId}`,
        3145728
      ),
      GetDocumentTotals: endpoint(
        (dto: server.dto.ListDocumentTotalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.GroupedDocumentTotals[], server.dto.ListDocumentTotalFilters>>(`/contract/stats/totalsdata`, dto, { systemOneApiConfig }),
        () => `${baseURL}/contract/stats/totalsdata`,
        3145728
      )
    },
    CostCenter: {
      ListCostCenter: endpoint(
        (dto: server.dto.PaginatedCostCenterInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.CostCenter[], server.dto.ListCostCenterFilters>>(`/costcenter/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/costcenter/list`,
        3145728
      ),
      UpdateCostCenter: endpoint(
        (dto: server.dto.UpdateCostCenterInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/costcenter/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/costcenter/update`,
        3145728
      ),
      CreateCostCenter: endpoint(
        (dto: server.dto.CreateCostCenterInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/costcenter/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/costcenter/create`,
        3145728
      ),
      GetCostCenter: endpoint(
        (costCenterId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.CostCenter>>(`/costcenter/${costCenterId}`, { systemOneApiConfig }),
        (costCenterId: number) => `${baseURL}/costcenter/${costCenterId}`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.CostCenterActivity[]>>(`/costcenter/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/costcenter/activity`,
        3145728
      )
    },
    Country: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedCountryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.CountryForList[], server.dto.ListCountryFilters>>(`/country/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/country/list`,
        3145728
      ),
      GetCountry: endpoint(
        (value: server.dto.Countries, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.Country>>(`/country/${value}`, { systemOneApiConfig }),
        (value: server.dto.Countries) => `${baseURL}/country/${value}`,
        3145728
      )
    },
    Credit: {
      ListCreditTransaction: endpoint(
        (dto: server.dto.PaginatedCreditTransactionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.CreditTransaction[], server.dto.ListCreditTransactionFilters>>(`/credit/listCreditTransaction`, dto, { systemOneApiConfig }),
        () => `${baseURL}/credit/listCreditTransaction`,
        3145728
      ),
      ListCreditGrant: endpoint(
        (dto: server.dto.PaginatedCreditGrantInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.CreditGrant[], server.dto.ListCreditGrantFilters>>(`/credit/listCreditGrant`, dto, { systemOneApiConfig }),
        () => `${baseURL}/credit/listCreditGrant`,
        3145728
      ),
      GetCreditAccountByClient: endpoint(
        (dto: server.dto.GetCreditAccountByClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.CreditAccount>>(`/credit/GetCreditAccountByClient`, dto, { systemOneApiConfig }),
        () => `${baseURL}/credit/GetCreditAccountByClient`,
        3145728
      ),
      ConfigAutoReload: endpoint(
        (dto: server.dto.ConfigAutoReloadCreditsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/credit/ConfigAutoReload`, dto, { systemOneApiConfig }),
        () => `${baseURL}/credit/ConfigAutoReload`,
        3145728
      ),
      PurchaseCredits: endpoint(
        (dto: server.dto.PurchaseCreditsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/credit/PurchaseCredits`, dto, { systemOneApiConfig }),
        () => `${baseURL}/credit/PurchaseCredits`,
        3145728
      ),
      AddPromoCredits: endpoint(
        (dto: server.dto.AddPromoCreditsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/credit/AddPromoCredits`, dto, { systemOneApiConfig }),
        () => `${baseURL}/credit/AddPromoCredits`,
        3145728
      )
    },
    Currency: {
      List: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.Currency[]>>(`/currency/list`, { systemOneApiConfig }),
        () => `${baseURL}/currency/list`,
        3145728
      )
    },
    CustomAppLabelTranslation: {
      Store: endpoint(
        (dto: server.dto.StoreCustomAppLabelTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.CustomAppLabelTranslation[]>>(`/customapplabeltranslation/store`, dto, { systemOneApiConfig }),
        () => `${baseURL}/customapplabeltranslation/store`,
        3145728
      ),
      Delete: endpoint(
        (clientId: number, translation: server.dto.CustomAppLabels, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapper<server.dto.CustomAppLabelTranslation[]>>(`/customapplabeltranslation/client/${clientId}/translations/${translation}/delete`, undefined, { systemOneApiConfig }),
        (clientId: number, translation: server.dto.CustomAppLabels) => `${baseURL}/customapplabeltranslation/client/${clientId}/translations/${translation}/delete`,
        3145728
      ),
      GetTranslation: endpoint(
        (clientId: number, translation: server.dto.CustomAppLabels, language: server.dto.Languages, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.CustomAppLabelTranslation>>(`/customapplabeltranslation/client/${clientId}/translations/${translation}/${language}`, { systemOneApiConfig }),
        (clientId: number, translation: server.dto.CustomAppLabels, language: server.dto.Languages) => `${baseURL}/customapplabeltranslation/client/${clientId}/translations/${translation}/${language}`,
        3145728
      )
    },
    Dashboard: {
      ShowsFromWebform: endpoint(
        (page: number, per_page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ShowFromWebform[]>>(`/dashboard/showsfromwebform`, { params: { page, per_page }, systemOneApiConfig }),
        (page: number, per_page: number) => `${baseURL}/dashboard/showsfromwebform?page=${page}&per_page=${per_page}`,
        3145728
      ),
      ShowsFromExternalInput: endpoint(
        (page: number, per_page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ShowFromExternalUser[]>>(`/dashboard/showsfromexternalinput`, { params: { page, per_page }, systemOneApiConfig }),
        (page: number, per_page: number) => `${baseURL}/dashboard/showsfromexternalinput?page=${page}&per_page=${per_page}`,
        3145728
      ),
      ESignForMe: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ESignForMeDashboard>>(`/dashboard/eSignForMe`, { systemOneApiConfig }),
        () => `${baseURL}/dashboard/eSignForMe`,
        3145728
      )
    },
    Diagnostic: {
      SetCache: endpoint(
        (value: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<string>(`/diagnostic/cache/set`, { params: { value }, systemOneApiConfig }),
        (value: string) => `${baseURL}/diagnostic/cache/set?value=${value}`,
        3145728
      ),
      GetCache: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<string>(`/diagnostic/cache/get`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/cache/get`,
        3145728
      ),
      ManagedCrash: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<void>(`/diagnostic/Exception/Managed`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/Exception/Managed`,
        3145728
      ),
      ManagedBackgroundThreadCrash: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<void>(`/diagnostic/Exception/ManagedBackgroundThread`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/Exception/ManagedBackgroundThread`,
        3145728
      ),
      LogSentryWarning: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<void>(`/diagnostic/SentryWarning`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/SentryWarning`,
        3145728
      ),
      Culture: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<string>(`/diagnostic/Culture`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/Culture`,
        3145728
      ),
      Health: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/diagnostic/Health`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/Health`,
        3145728
      ),
      TestSendServiceBusCommand: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/diagnostic/Test/ServiceBus`, undefined, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/Test/ServiceBus`,
        3145728
      ),
      SendTestSignalR: endpoint(
        (badgeCount: number, recipientUserId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/diagnostic/Test/SignalR`, undefined, { params: { badgeCount, recipientUserId }, systemOneApiConfig }),
        (badgeCount: number, recipientUserId: number) => `${baseURL}/diagnostic/Test/SignalR?badgeCount=${badgeCount}&recipientUserId=${recipientUserId}`,
        3145728
      ),
      SendTestNotification: endpoint(
        (dto: server.dto.SendTestNotificationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<string>(`/diagnostic/Test/Notification`, dto, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/Test/Notification`,
        3145728
      ),
      Serialize: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.SerializationCheck>(`/diagnostic/Serialize`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/Serialize`,
        3145728
      ),
      MockEmail: endpoint(
        (type: server.dto.MakeMockEmails, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/diagnostic/mockEmail`, { params: { type }, systemOneApiConfig }),
        (type: server.dto.MakeMockEmails) => `${baseURL}/diagnostic/mockEmail?type=${type}`,
        3145728
      ),
      ListClaims: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<string[]>(`/diagnostic/Claims`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/Claims`,
        3145728
      ),
      AuthorizationContext: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<string[]>(`/diagnostic/AuthorizationContext`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/AuthorizationContext`,
        3145728
      ),
      GeoCodeHealth: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.GeoCodeHealth>(`/diagnostic/geocode/health`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/geocode/health`,
        3145728
      ),
      SendBadge: endpoint(
        (recipientId: number, badgeCount: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/diagnostic/NotificationHub/SendBadge`, undefined, { params: { recipientId, badgeCount }, systemOneApiConfig }),
        (recipientId: number, badgeCount: number) => `${baseURL}/diagnostic/NotificationHub/SendBadge?recipientId=${recipientId}&badgeCount=${badgeCount}`,
        3145728
      ),
      SendTestMobileNotification: endpoint(
        (recipientId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/diagnostic/NotificationHub/SendNotification`, undefined, { params: { recipientId }, systemOneApiConfig }),
        (recipientId: number) => `${baseURL}/diagnostic/NotificationHub/SendNotification?recipientId=${recipientId}`,
        3145728
      ),
      GetNotificationHubRegistrations: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/diagnostic/NotificationHub/Registrations`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/NotificationHub/Registrations`,
        3145728
      ),
      TestGet: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/diagnostic/TestGet`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/TestGet`,
        3145728
      ),
      TestPost: endpoint(
        (dto: server.dto.DiagnosticInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/diagnostic/TestPost`, dto, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/TestPost`,
        3145728
      ),
      TestPut: endpoint(
        (dto: server.dto.DiagnosticInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapper<string>>(`/diagnostic/TestPut`, dto, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/TestPut`,
        3145728
      ),
      GetHeaders: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/diagnostic/debug-headers`, { systemOneApiConfig }),
        () => `${baseURL}/diagnostic/debug-headers`,
        3145728
      )
    },
    Document: {
      Lock: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/document/${documentId}/lock`, undefined, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/document/${documentId}/lock`,
        3145728
      ),
      Unlock: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/document/${documentId}/unlock`, undefined, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/document/${documentId}/unlock`,
        3145728
      ),
      ListDocumentsByShow: endpoint(
        (dto: server.dto.ListDocumentsByShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.DocumentForList[]>>(`/document/listByShow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/document/listByShow`,
        3145728
      )
    },
    EmailMessage: {
      SendEmailMessageCommand: endpoint(
        (instruction: server.dto.SendEmailMessageInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/emailmessage/send`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/emailmessage/send`,
        3145728
      ),
      DeleteEmailMessage: endpoint(
        (emailMessageId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/emailmessage/${emailMessageId}`, { systemOneApiConfig }),
        (emailMessageId: number) => `${baseURL}/emailmessage/${emailMessageId}`,
        3145728
      )
    },
    EmailTemplate: {

    },
    ESignAgencyDashboard: {
      Dashboard: endpoint(
        (dto: server.dto.ListESignAgencyDashboardInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ESignAgencyDashboard>>(`/esignagencydashboard/dashboard`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esignagencydashboard/dashboard`,
        3145728
      )
    },
    ESign: {
      Create: endpoint(
        (dto: server.dto.CreateESignInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/esign/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/create`,
        3145728
      ),
      CreateFromShow: endpoint(
        (dto: server.dto.CreateESignFromShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/esign/create/fromshow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/create/fromshow`,
        3145728
      ),
      UpdateDocument: endpoint(
        (dto: server.dto.UpdateESignDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/document/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/document/update`,
        3145728
      ),
      ChangeESignToDraftStage: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/document/${documentId}/toDraft`, undefined, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/esign/document/${documentId}/toDraft`,
        3145728
      ),
      ChangeESignToReviewStage: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/document/${documentId}/toReview`, undefined, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/esign/document/${documentId}/toReview`,
        3145728
      ),
      ChangeESignToOutForSignature: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/document/${documentId}/toOutForSignature`, undefined, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/esign/document/${documentId}/toOutForSignature`,
        3145728
      ),
      ChangeESignToDiscardedStage: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/document/${documentId}/toDiscarded`, undefined, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/esign/document/${documentId}/toDiscarded`,
        3145728
      ),
      ManualComplete: endpoint(
        (instruction: server.dto.UploadCompletedESignPdfInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/esign/document/manualComplete`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/esign/document/manualComplete`,
        209715200
      ),
      SetSenderFromUser: endpoint(
        (dto: server.dto.SetESignSenderFromUserInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ESignParticipant>>(`/esign/setSenderFromUser`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/setSenderFromUser`,
        3145728
      ),
      SetParticipantFromContact: endpoint(
        (dto: server.dto.SetESignParticipantFromContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ESignParticipant>>(`/esign/participant/set/fromContact`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/participant/set/fromContact`,
        3145728
      ),
      DeleteParticipant: endpoint(
        (eSignParticipantId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/participant/${eSignParticipantId}/delete`, undefined, { systemOneApiConfig }),
        (eSignParticipantId: number) => `${baseURL}/esign/participant/${eSignParticipantId}/delete`,
        3145728
      ),
      ListParticipants: endpoint(
        (dto: server.dto.ListESignParticipantInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ESignParticipant[]>>(`/esign/participant/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/participant/list`,
        3145728
      ),
      GetLastSignature: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.LastSignature>>(`/esign/signature/last`, { systemOneApiConfig }),
        () => `${baseURL}/esign/signature/last`,
        3145728
      ),
      ListESignFiles: endpoint(
        (documentId: number, fileGroup: server.dto.ESignFileGroups, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ESignFile[]>>(`/esign/document/${documentId}/files/${fileGroup}/list`, { systemOneApiConfig }),
        (documentId: number, fileGroup: server.dto.ESignFileGroups) => `${baseURL}/esign/document/${documentId}/files/${fileGroup}/list`,
        3145728
      ),
      CreateESignFileFromTemplate: endpoint(
        (dto: server.dto.CreateESignFileFromTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ESignFile[]>>(`/esign/file/create/fromTemplate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/file/create/fromTemplate`,
        3145728
      ),
      CreateESignFilesFromStorage: endpoint(
        (dto: server.dto.CreateESignFileFromStorageInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ESignFile[]>>(`/esign/file/create/fromStorage`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/file/create/fromStorage`,
        3145728
      ),
      CreateESignFilesFromUpload: endpoint(
        (instruction: server.dto.UploadMultipleESignFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/file/create/fromUpload`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/esign/file/create/fromUpload`,
        209715200
      ),
      DeleteESignFile: endpoint(
        (eSignFileId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/file/${eSignFileId}/delete`, undefined, { systemOneApiConfig }),
        (eSignFileId: number) => `${baseURL}/esign/file/${eSignFileId}/delete`,
        3145728
      ),
      UpdateESignFileName: endpoint(
        (dto: server.dto.UpdateESignFileNameInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ESignFile[]>>(`/esign/file/update/name`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/file/update/name`,
        3145728
      ),
      UpdateESignFileOrder: endpoint(
        (dto: server.dto.UpdateESignFileOrderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/file/sort`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/file/sort`,
        3145728
      ),
      DownloadESignFile: endpoint(
        (fileId: string, fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/esign/file/${fileId}/download/${fileName}`, { systemOneApiConfig }),
        (fileId: string, fileName: string) => `${baseURL}/esign/file/${fileId}/download/${fileName}`,
        3145728
      ),
      DownloadStitchedFile: endpoint(
        (documentId: number, fileName: string, inline: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/esign/document/${documentId}/downloadStitched/${fileName}`, { params: { inline }, systemOneApiConfig }),
        (documentId: number, fileName: string, inline: boolean) => `${baseURL}/esign/document/${documentId}/downloadStitched/${fileName}?inline=${inline}`,
        3145728
      ),
      ListESignForMe: endpoint(
        (dto: server.dto.ListESignForMeInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ESignForMe[]>>(`/esign/forMe/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/forMe/list`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ESignActivity[]>>(`/esign/document/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/document/activity`,
        3145728
      ),
      ListReminders: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.DocumentReminder[]>>(`/esign/document/${documentId}/reminder`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/esign/document/${documentId}/reminder`,
        3145728
      ),
      DeleteAllReminders: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/document/${documentId}/reminder/clear/all`, undefined, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/esign/document/${documentId}/reminder/clear/all`,
        3145728
      ),
      DeleteLastReminder: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/document/${documentId}/reminder/clear/last`, undefined, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/esign/document/${documentId}/reminder/clear/last`,
        3145728
      ),
      AddReminder: endpoint(
        (dto: server.dto.AddDocumentReminderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/reminder/add`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/reminder/add`,
        3145728
      ),
      UpdateReminder: endpoint(
        (dto: server.dto.UpdateDocumentReminderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/esign/reminder/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/esign/reminder/update`,
        3145728
      )
    },
    ESignOpen: {
      PlaceSignature: endpoint(
        (secret: string, dto: server.dto.PlaceSignatureInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.PlaceSignatureOutcome>>(`/esign/open/participant/${secret}/signature`, dto, { systemOneApiConfig }),
        (secret: string) => `${baseURL}/esign/open/participant/${secret}/signature`,
        3145728
      ),
      DownloadESignPdfBySecret: endpoint(
        (secret: string, fileName: string, inline: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/esign/open/participant/${secret}/download/pdf/${fileName}`, { params: { inline }, systemOneApiConfig }),
        (secret: string, fileName: string, inline: boolean) => `${baseURL}/esign/open/participant/${secret}/download/pdf/${fileName}?inline=${inline}`,
        3145728
      ),
      DownloadOtherFileBySecret: endpoint(
        (secret: string, fileId: string, fileName: string, inline: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/esign/open/participant/${secret}/download/${fileId}/other/${fileName}`, { params: { inline }, systemOneApiConfig }),
        (secret: string, fileId: string, fileName: string, inline: boolean) => `${baseURL}/esign/open/participant/${secret}/download/${fileId}/other/${fileName}?inline=${inline}`,
        3145728
      ),
      UploadSignatureFile: endpoint(
        (instruction: server.dto.UploadSignatureInstruction, secret: string, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/esign/open/participant/${secret}/signature/upload`, instruction, { systemOneApiConfig }),
        (secret: string) => `${baseURL}/esign/open/participant/${secret}/signature/upload`,
        10485760
      ),
      DownloadSignatureFile: endpoint(
        (fileId: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/esign/open/signature/${fileId}/download/signature.png`, { systemOneApiConfig }),
        (fileId: string) => `${baseURL}/esign/open/signature/${fileId}/download/signature.png`,
        3145728
      )
    },
    EuVatNumber: {
      RecheckCompanyVatNumber: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.EuVatNumber>>(`/euvatnumber/company/${companyId}/recheck`, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/euvatnumber/company/${companyId}/recheck`,
        3145728
      ),
      UpdateCompanyVatNumber: endpoint(
        (companyId: number, number: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/euvatnumber/company/${companyId}/update`, { params: { number }, systemOneApiConfig }),
        (companyId: number, number: string) => `${baseURL}/euvatnumber/company/${companyId}/update?number=${number}`,
        3145728
      ),
      CompanyVatNumberHealth: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.VatNumberHealth>>(`/euvatnumber/company/health`, { systemOneApiConfig }),
        () => `${baseURL}/euvatnumber/company/health`,
        3145728
      )
    },
    ExcelTemplate: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedExcelTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ExcelTemplate[], server.dto.ListExcelTemplateFilters>>(`/exceltemplate/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/exceltemplate/list`,
        3145728
      ),
      DeleteExcelTemplate: endpoint(
        (dto: server.dto.DeleteExcelTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/exceltemplate/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/exceltemplate/delete`,
        3145728
      ),
      CreateExcelTemplate: endpoint(
        (dto: server.dto.CreateExcelTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/exceltemplate/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/exceltemplate/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateExcelTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/exceltemplate/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/exceltemplate/update`,
        3145728
      ),
      GetExcelTemplate: endpoint(
        (excelTemplateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExcelTemplate>>(`/exceltemplate/${excelTemplateId}`, { systemOneApiConfig }),
        (excelTemplateId: number) => `${baseURL}/exceltemplate/${excelTemplateId}`,
        3145728
      ),
      Copy: endpoint(
        (dto: server.dto.CopyExcelTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/exceltemplate/copy`, dto, { systemOneApiConfig }),
        () => `${baseURL}/exceltemplate/copy`,
        3145728
      ),
      AddRemoveColumns: endpoint(
        (dto: server.dto.AddRemoveExcelTemplateColumnsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/exceltemplate/addRemoveColumns`, dto, { systemOneApiConfig }),
        () => `${baseURL}/exceltemplate/addRemoveColumns`,
        3145728
      ),
      UpdateColumn: endpoint(
        (dto: server.dto.UpdateExcelColumnInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/exceltemplate/updateColumn`, dto, { systemOneApiConfig }),
        () => `${baseURL}/exceltemplate/updateColumn`,
        3145728
      ),
      UpdateColumnOrder: endpoint(
        (dto: server.dto.UpdateExcelTemplateColumnOrderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/exceltemplate/column/order`, dto, { systemOneApiConfig }),
        () => `${baseURL}/exceltemplate/column/order`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExcelTemplateActivity[]>>(`/exceltemplate/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/exceltemplate/activity`,
        3145728
      )
    },
    ExternalCalendar: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedExternalCalendarInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ExternalCalendarForList[], server.dto.ListExternalCalendarFilters>>(`/externalcalendar/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalcalendar/list`,
        3145728
      ),
      ListRefreshLog: endpoint(
        (dto: server.dto.PaginatedExternalCalendarRefreshLogInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExternalCalendarRefresh[]>>(`/externalcalendar/refreshLog`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalcalendar/refreshLog`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateExternalCalendarInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/externalcalendar/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalcalendar/update`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteExternalCalendarInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/externalcalendar/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalcalendar/delete`,
        3145728
      ),
      SubscribeToICalendarUrl: endpoint(
        (dto: server.dto.SubscribeToICalendarUrlInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/externalcalendar/SubscribeToICalendarUrl`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalcalendar/SubscribeToICalendarUrl`,
        3145728
      ),
      SubscribeToGoogleCalendar: endpoint(
        (dto: server.dto.SubscribeToGoogleCalendarInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/externalcalendar/SubscribeToGoogleCalendar`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalcalendar/SubscribeToGoogleCalendar`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExternalCalendarActivity[]>>(`/externalcalendar/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalcalendar/activity`,
        3145728
      )
    },
    ExternalEvent: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedExternalEventInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ExternalEvent[], server.dto.ListExternalEventFilters>>(`/externalevent/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalevent/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (externalEventId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExternalEventSidePanelData>>(`/externalevent/${externalEventId}/GetSidePanelData`, { systemOneApiConfig }),
        (externalEventId: number) => `${baseURL}/externalevent/${externalEventId}/GetSidePanelData`,
        3145728
      )
    },
    ExternalIdentity: {
      Disconnect: endpoint(
        (dto: server.dto.DisconnectExternalIdentityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/externalidentity/disconnect`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalidentity/disconnect`,
        3145728
      ),
      ConnectGoogle: endpoint(
        (dto: server.dto.ConnectGoogleExternalIdentityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ConnectedExternalIdentities>>(`/externalidentity/connect/google`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalidentity/connect/google`,
        3145728
      ),
      ConnectApple: endpoint(
        (dto: server.dto.ConnectAppleExternalIdentityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ConnectedExternalIdentities>>(`/externalidentity/connect/apple`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalidentity/connect/apple`,
        3145728
      ),
      SignIn: endpoint(
        (dto: server.dto.SignInExternalIdentityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/externalidentity/signin`, dto, { systemOneApiConfig }),
        () => `${baseURL}/externalidentity/signin`,
        3145728
      )
    },
    Extractor: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedExtractorSessionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ExtractorSession[], server.dto.ListExtractorSessionFilters>>(`/extractor/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/list`,
        3145728
      ),
      DownloadSessionFile: endpoint(
        (extractorSessionId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/extractor/sessionFile/${extractorSessionId}/download`, { systemOneApiConfig }),
        (extractorSessionId: number) => `${baseURL}/extractor/sessionFile/${extractorSessionId}/download`,
        3145728
      ),
      GetExtractorSession: endpoint(
        (extractorSessionId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExtractorSession>>(`/extractor/GetExtractorSession`, { params: { extractorSessionId }, systemOneApiConfig }),
        (extractorSessionId: number) => `${baseURL}/extractor/GetExtractorSession?extractorSessionId=${extractorSessionId}`,
        3145728
      ),
      StartSessionWithFile: endpoint(
        (dto: server.dto.StartExtractorSessionWithFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/extractor/StartSessionWithFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/StartSessionWithFile`,
        10485760
      ),
      StartSessionWithText: endpoint(
        (dto: server.dto.StartExtractorSessionWithTextInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/extractor/StartSessionWithText`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/StartSessionWithText`,
        3145728
      ),
      ResendSession: endpoint(
        (dto: server.dto.ResendExtractorSessionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/extractor/ResendSession`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/ResendSession`,
        3145728
      ),
      DeleteSession: endpoint(
        (dto: server.dto.DeleteExtractorSessionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/extractor/DeleteSession`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/DeleteSession`,
        3145728
      ),
      FillSession: endpoint(
        (dto: server.dto.FillExtractorInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/extractor/FillSession`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/FillSession`,
        3145728
      ),
      TriggerProcessSession: endpoint(
        (dto: server.dto.TriggerProcessExtractorSessionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/extractor/triggerProcessSession`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/triggerProcessSession`,
        3145728
      ),
      GetExtractedFlight: endpoint(
        (extractedFlightId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExtractedFlight>>(`/extractor/GetExtractedFlight`, { params: { extractedFlightId }, systemOneApiConfig }),
        (extractedFlightId: number) => `${baseURL}/extractor/GetExtractedFlight?extractedFlightId=${extractedFlightId}`,
        3145728
      ),
      UpdateExtractedFlight: endpoint(
        (dto: server.dto.UpdateExtractedFlightInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/extractor/UpdateExtractedFlight`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/UpdateExtractedFlight`,
        3145728
      ),
      DeleteExtractedFlight: endpoint(
        (dto: server.dto.DeleteExtractedFlightInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/extractor/DeleteExtractedFlight`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/DeleteExtractedFlight`,
        3145728
      ),
      UpdateExtractedFlightPassenger: endpoint(
        (dto: server.dto.UpdateExtractedFlightPassengerInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/extractor/UpdateExtractedFlightPassenger`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/UpdateExtractedFlightPassenger`,
        3145728
      ),
      DeleteExtractedFlightPassenger: endpoint(
        (dto: server.dto.DeleteExtractedFlightPassengerInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/extractor/DeleteExtractedFlightPassenger`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/DeleteExtractedFlightPassenger`,
        3145728
      ),
      ApproveExtractedFlight: endpoint(
        (extractedFlightId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<number>>(`/extractor/ApproveExtractedFlight`, { params: { extractedFlightId }, systemOneApiConfig }),
        (extractedFlightId: number) => `${baseURL}/extractor/ApproveExtractedFlight?extractedFlightId=${extractedFlightId}`,
        3145728
      ),
      CreateContactForExtractedFlightPassenger: endpoint(
        (dto: server.dto.CreateContactForExtractedFlightPassengerInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/extractor/CreateContactForExtractedFlightPassenger`, dto, { systemOneApiConfig }),
        () => `${baseURL}/extractor/CreateContactForExtractedFlightPassenger`,
        3145728
      )
    },
    FavoriteArtist: {
      Add: endpoint(
        (instruction: server.dto.AddFavoriteArtistInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/favoriteartist/add`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/favoriteartist/add`,
        3145728
      ),
      Remove: endpoint(
        (instruction: server.dto.RemoveFavoriteArtistInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/favoriteartist/remove`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/favoriteartist/remove`,
        3145728
      )
    },
    FavoriteSubject: {
      ListSubjects: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FavoriteSubject[]>>(`/favoritesubject`, { systemOneApiConfig }),
        () => `${baseURL}/favoritesubject`,
        3145728
      ),
      DeleteSubject: endpoint(
        (id: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/favoritesubject/${id}`, { systemOneApiConfig }),
        (id: number) => `${baseURL}/favoritesubject/${id}`,
        3145728
      ),
      CreateSubject: endpoint(
        (dto: server.dto.CreateFavoriteSubjectInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FavoriteSubject[]>>(`/favoritesubject/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/favoritesubject/create`,
        3145728
      ),
      UpdateSubject: endpoint(
        (dto: server.dto.UpdateFavoriteSubjectInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapper<server.dto.FavoriteSubject[]>>(`/favoritesubject`, dto, { systemOneApiConfig }),
        () => `${baseURL}/favoritesubject`,
        3145728
      )
    },
    File: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedClientFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.File[], server.dto.ListClientFileFilters>>(`/file/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/file/list`,
        3145728
      ),
      DownloadEmailAttachments: endpoint(
        (secret: string, fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/file/email/${secret}/download/${fileName}`, { systemOneApiConfig }),
        (secret: string, fileName: string) => `${baseURL}/file/email/${secret}/download/${fileName}`,
        3145728
      ),
      DownloadClientFile: endpoint(
        (fileId: string, fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/file/clientfile/${fileId}/download/${fileName}`, { systemOneApiConfig }),
        (fileId: string, fileName: string) => `${baseURL}/file/clientfile/${fileId}/download/${fileName}`,
        3145728
      ),
      DeleteClientFile: endpoint(
        (clientFileId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/file/clientfile/${clientFileId}/delete`, { systemOneApiConfig }),
        (clientFileId: number) => `${baseURL}/file/clientfile/${clientFileId}/delete`,
        3145728
      ),
      SetClientFileProperties: endpoint(
        (dto: server.dto.SetClientFilePropertiesInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/file/clientfile/setproperties`, dto, { systemOneApiConfig }),
        () => `${baseURL}/file/clientfile/setproperties`,
        3145728
      ),
      UploadSystemWideClientFiles: endpoint(
        (instruction: server.dto.UploadMultipleSystemWideFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/file/clientfile/systemwide/upload`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/file/clientfile/systemwide/upload`,
        209715200
      ),
      ListSystemWideClientFiles: endpoint(
        (page: number, per_page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.File[]>>(`/file/clientfile/systemwide/list`, { params: { page, per_page }, systemOneApiConfig }),
        (page: number, per_page: number) => `${baseURL}/file/clientfile/systemwide/list?page=${page}&per_page=${per_page}`,
        3145728
      ),
      UploadClientFiles: endpoint(
        (instruction: server.dto.UploadMultipleClientFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/file/clientfile/files/upload`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/file/clientfile/files/upload`,
        209715200
      )
    },
    FilterPreset: {
      DeleteFilterPreset: endpoint(
        (filterPresetId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/filterpreset/${filterPresetId}/delete`, { systemOneApiConfig }),
        (filterPresetId: number) => `${baseURL}/filterpreset/${filterPresetId}/delete`,
        3145728
      ),
      CreateFilterPresetForShow: endpoint(
        (dto: server.dto.CreateFilterPresetInstruction<server.dto.PaginatedShowInternalInstruction>, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/filterpreset/show/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/show/create`,
        3145728
      ),
      CreateFilterPresetForPlanningByWeek: endpoint(
        (dto: server.dto.CreateFilterPresetInstruction<server.dto.PlanningByWeekQuery>, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/filterpreset/planningByWeek/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/planningByWeek/create`,
        3145728
      ),
      CreateFilterPresetForPlanningByMonth: endpoint(
        (dto: server.dto.CreateFilterPresetInstruction<server.dto.PlanningByMonthQuery>, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/filterpreset/planningByMonth/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/planningByMonth/create`,
        3145728
      ),
      CreateFilterPresetForPlanningByQuarter: endpoint(
        (dto: server.dto.CreateFilterPresetInstruction<server.dto.PlanningByQuarterQuery>, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/filterpreset/planningByQuarter/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/planningByQuarter/create`,
        3145728
      ),
      CreateFilterPresetForInvoice: endpoint(
        (dto: server.dto.CreateFilterPresetInstruction<server.dto.PaginatedInvoiceInstruction>, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/filterpreset/invoice/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/invoice/create`,
        3145728
      ),
      CreateFilterPresetForCompany: endpoint(
        (dto: server.dto.CreateFilterPresetInstruction<server.dto.PaginatedCompanyInstruction>, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/filterpreset/company/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/company/create`,
        3145728
      ),
      CreateFilterPresetForContact: endpoint(
        (dto: server.dto.CreateFilterPresetInstruction<server.dto.PaginatedContactInstruction>, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/filterpreset/contact/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/contact/create`,
        3145728
      ),
      CreateFilterPresetForDocument: endpoint(
        (dto: server.dto.CreateFilterPresetInstruction<server.dto.PaginatedDocumentInstruction>, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/filterpreset/document/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/document/create`,
        3145728
      ),
      ListFilterPresetsForShow: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PaginatedShowInternalInstruction>[]>>(`/filterpreset/show/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/show/list`,
        3145728
      ),
      ListFilterPresetsForPlanningByWeek: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PlanningByWeekQuery>[]>>(`/filterpreset/planningByWeek/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/planningByWeek/list`,
        3145728
      ),
      ListFilterPresetsForPlanningByMonth: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PlanningByMonthQuery>[]>>(`/filterpreset/planningByMonth/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/planningByMonth/list`,
        3145728
      ),
      ListFilterPresetsForPlanningByQuarter: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PlanningByQuarterQuery>[]>>(`/filterpreset/planningByQuarter/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/planningByQuarter/list`,
        3145728
      ),
      ListFilterPresetsForInvoice: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PaginatedInvoiceInstruction>[]>>(`/filterpreset/invoice/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/invoice/list`,
        3145728
      ),
      ListFilterPresetsForCompany: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PaginatedCompanyInstruction>[]>>(`/filterpreset/company/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/company/list`,
        3145728
      ),
      ListFilterPresetsForContact: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PaginatedContactInstruction>[]>>(`/filterpreset/contact/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/contact/list`,
        3145728
      ),
      ListFilterPresetsForQuote: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PaginatedDocumentInstruction>[]>>(`/filterpreset/quote/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/quote/list`,
        3145728
      ),
      ListFilterPresetsForPurchaseAgreement: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PaginatedDocumentInstruction>[]>>(`/filterpreset/purchaseAgreement/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/purchaseAgreement/list`,
        3145728
      ),
      ListFilterPresetsForContract: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FilterPreset<server.dto.PaginatedDocumentInstruction>[]>>(`/filterpreset/contract/list`, { systemOneApiConfig }),
        () => `${baseURL}/filterpreset/contract/list`,
        3145728
      )
    },
    Flight: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedFlightInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.FlightForList[], server.dto.ListFlightFilters>>(`/flight/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flight/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (flightId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FlightSidePanelData>>(`/flight/${flightId}/SidePanel`, { systemOneApiConfig }),
        (flightId: number) => `${baseURL}/flight/${flightId}/SidePanel`,
        3145728
      ),
      CreateFlight: endpoint(
        (dto: server.dto.CreateFlightInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/flight/createFlight`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flight/createFlight`,
        3145728
      ),
      UpdateFlight: endpoint(
        (dto: server.dto.UpdateFlightInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/flight/updateFlight`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flight/updateFlight`,
        3145728
      ),
      CopyFlight: endpoint(
        (dto: server.dto.CopyFlightInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/flight/copyFlight`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flight/copyFlight`,
        3145728
      ),
      DeleteFlight: endpoint(
        (dto: server.dto.DeleteFlightInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/flight/deleteFlight`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flight/deleteFlight`,
        3145728
      ),
      RetrieveFlightFromFlightStats: endpoint(
        (dto: server.dto.RetrieveFlightFromFlightStatsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ScheduledFlightFromFlightStats[]>>(`/flight/RetrieveFlightFromFlightStats`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flight/RetrieveFlightFromFlightStats`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FlightActivity[]>>(`/flight/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flight/activity`,
        3145728
      )
    },
    FlightPassenger: {
      GetSidePanelData: endpoint(
        (flightPassengerId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FlightPassengerSidePanelData>>(`/flightpassenger/${flightPassengerId}/SidePanel`, undefined, { systemOneApiConfig }),
        (flightPassengerId: number) => `${baseURL}/flightpassenger/${flightPassengerId}/SidePanel`,
        3145728
      ),
      CreatePassenger: endpoint(
        (dto: server.dto.CreateFlightPassengerInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/flightpassenger/createPassenger`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flightpassenger/createPassenger`,
        3145728
      ),
      UpdatePassenger: endpoint(
        (dto: server.dto.UpdateFlightPassengerInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/flightpassenger/updatePassenger`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flightpassenger/updatePassenger`,
        3145728
      ),
      DeletePassenger: endpoint(
        (dto: server.dto.DeleteFlightPassengerInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/flightpassenger/deletePassenger`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flightpassenger/deletePassenger`,
        3145728
      ),
      UploadPassengerFiles: endpoint(
        (dto: server.dto.UploadMultiplePassengerFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/flightpassenger/uploadPassengerFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flightpassenger/uploadPassengerFile`,
        10485760
      ),
      DownloadPassengerFile: endpoint(
        (fileId: string, fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/flightpassenger/${fileId}/download/${fileName}`, { systemOneApiConfig }),
        (fileId: string, fileName: string) => `${baseURL}/flightpassenger/${fileId}/download/${fileName}`,
        3145728
      ),
      DeletePassengerFile: endpoint(
        (dto: server.dto.DeleteFlightPassengerFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/flightpassenger/deletePassengerFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flightpassenger/deletePassengerFile`,
        3145728
      ),
      UpdatePassengerFileName: endpoint(
        (dto: server.dto.UpdateFlightPassengerFileNameInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/flightpassenger/updatePassengerFileName`, dto, { systemOneApiConfig }),
        () => `${baseURL}/flightpassenger/updatePassengerFileName`,
        3145728
      )
    },
    FreeFieldCategory: {
      List: endpoint(
        (mainArea: server.dto.MainAreas, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FreeFieldCategoryForList[]>>(`/freefieldcategory/list`, { params: { mainArea }, systemOneApiConfig }),
        (mainArea: server.dto.MainAreas) => `${baseURL}/freefieldcategory/list?mainArea=${mainArea}`,
        3145728
      ),
      Sort: endpoint(
        (dto: server.dto.SortFreeFieldCategoryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefieldcategory/sort`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldcategory/sort`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateFreeFieldCategoryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/freefieldcategory/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldcategory/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateFreeFieldCategoryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefieldcategory/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldcategory/update`,
        3145728
      ),
      DeleteAndMove: endpoint(
        (dto: server.dto.DeleteFreeFieldCategoryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefieldcategory/deleteAndMove`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldcategory/deleteAndMove`,
        3145728
      ),
      StoreSortedDefinitions: endpoint(
        (dto: server.dto.SortFreeFieldCategoryDefinitionsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefieldcategory/definitions/sort`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldcategory/definitions/sort`,
        3145728
      )
    },
    FreeFieldDefinition: {
      Create: endpoint(
        (dto: server.dto.CreateFreeFieldDefinitionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/freefielddefinition/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefielddefinition/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateFreeFieldDefinitionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefielddefinition/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefielddefinition/update`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteFreeFieldDefinitionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefielddefinition/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefielddefinition/delete`,
        3145728
      ),
      ReferenceExists: endpoint(
        (dto: server.dto.ReferenceExistsFreeFieldDefinitionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<boolean>>(`/freefielddefinition/referenceExists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefielddefinition/referenceExists`,
        3145728
      ),
      AssignProfileLevelsToFreeFieldDefinition: endpoint(
        (dto: server.dto.SetFreeFieldProfilesForFreeFieldInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefielddefinition/profiles/assignlevels`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefielddefinition/profiles/assignlevels`,
        3145728
      )
    },
    FreeFieldProfile: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedFreeFieldProfileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.FreeFieldProfileForList[], server.dto.ListFreeFieldProfileFilters>>(`/freefieldprofile/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldprofile/list`,
        3145728
      ),
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedFreeFieldProfileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.FreeFieldProfileSmall[], server.dto.ListFreeFieldProfileFilters>>(`/freefieldprofile/listSmall`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldprofile/listSmall`,
        3145728
      ),
      GetProfile: endpoint(
        (freeFieldProfileId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FreeFieldProfile>>(`/freefieldprofile/${freeFieldProfileId}`, { systemOneApiConfig }),
        (freeFieldProfileId: number) => `${baseURL}/freefieldprofile/${freeFieldProfileId}`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (freeFieldProfileId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.FreeFieldProfileSidePanelData>>(`/freefieldprofile/${freeFieldProfileId}/SidePanel`, { systemOneApiConfig }),
        (freeFieldProfileId: number) => `${baseURL}/freefieldprofile/${freeFieldProfileId}/SidePanel`,
        3145728
      ),
      ListUserToClientsByProfile: endpoint(
        (freeFieldProfileId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserToClientSmall[]>>(`/freefieldprofile/${freeFieldProfileId}/userToClients`, { systemOneApiConfig }),
        (freeFieldProfileId: number) => `${baseURL}/freefieldprofile/${freeFieldProfileId}/userToClients`,
        3145728
      ),
      CreateProfile: endpoint(
        (dto: server.dto.CreateFreeFieldProfileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/freefieldprofile/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldprofile/create`,
        3145728
      ),
      UpdateProfile: endpoint(
        (dto: server.dto.UpdateFreeFieldProfileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefieldprofile/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldprofile/update`,
        3145728
      ),
      SetLevelForFreeFields: endpoint(
        (dto: server.dto.SetPermissionLevelForFreeFieldsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefieldprofile/setPermissionLevel`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldprofile/setPermissionLevel`,
        3145728
      ),
      DeleteFreeFieldProfile: endpoint(
        (dto: server.dto.DeleteFreeFieldProfileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefieldprofile/deleteFreeFieldProfile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldprofile/deleteFreeFieldProfile`,
        3145728
      ),
      RemoveFreeFieldProfileForUser: endpoint(
        (dto: server.dto.RemoveFreeFieldProfileForUserInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/freefieldprofile/RemoveFreeFieldProfileForUser`, dto, { systemOneApiConfig }),
        () => `${baseURL}/freefieldprofile/RemoveFreeFieldProfileForUser`,
        3145728
      )
    },
    Google: {
      SignIn: endpoint(
        (returnUrl: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/google/signin`, { params: { returnUrl }, systemOneApiConfig }),
        (returnUrl: string) => `${baseURL}/google/signin?returnUrl=${returnUrl}`,
        3145728
      ),
      SignInOrRegister: endpoint(
        (returnUrl: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/google/register`, { params: { returnUrl }, systemOneApiConfig }),
        (returnUrl: string) => `${baseURL}/google/register?returnUrl=${returnUrl}`,
        3145728
      ),
      Connect: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/google/connect`, { systemOneApiConfig }),
        () => `${baseURL}/google/connect`,
        3145728
      ),
      ConnectCalendar: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/google/connect/calendar`, { systemOneApiConfig }),
        () => `${baseURL}/google/connect/calendar`,
        3145728
      ),
      MyDetails: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.GoogleIdentityUserDetails>>(`/google/details`, { systemOneApiConfig }),
        () => `${baseURL}/google/details`,
        3145728
      ),
      ListCalendars: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.GoogleCalendar[]>>(`/google/listCalendars`, { systemOneApiConfig }),
        () => `${baseURL}/google/listCalendars`,
        3145728
      )
    },
    GoogleDirections: {
      FetchDirections: endpoint(
        (dto: server.dto.GetGoogleDirectionsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.GoogleDirections>>(`/googledirections/fetchDirections`, dto, { systemOneApiConfig }),
        () => `${baseURL}/googledirections/fetchDirections`,
        3145728
      )
    },
    GooglePlace: {
      Autocomplete: endpoint(
        (dto: server.dto.GooglePlaceAutocompleteInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.GooglePlaceAutocomplete[]>>(`/googleplace/autocomplete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/googleplace/autocomplete`,
        3145728
      ),
      GetPlace: endpoint(
        (dto: server.dto.GetGooglePlaceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.GooglePlace>>(`/googleplace/getGooglePlace`, dto, { systemOneApiConfig }),
        () => `${baseURL}/googleplace/getGooglePlace`,
        3145728
      )
    },
    GroundTransport: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedGroundTransportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.GroundTransportForList[], server.dto.ListGroundTransportFilters>>(`/groundtransport/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/groundtransport/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (groundTransportId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.GroundTransportSidePanelData>>(`/groundtransport/${groundTransportId}/SidePanel`, { systemOneApiConfig }),
        (groundTransportId: number) => `${baseURL}/groundtransport/${groundTransportId}/SidePanel`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateGroundTransportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/groundtransport/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/groundtransport/create`,
        3145728
      ),
      Copy: endpoint(
        (dto: server.dto.CopyGroundTransportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/groundtransport/copy`, dto, { systemOneApiConfig }),
        () => `${baseURL}/groundtransport/copy`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateGroundTransportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/groundtransport/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/groundtransport/update`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteGroundTransportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/groundtransport/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/groundtransport/delete`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.GroundTransportActivity[]>>(`/groundtransport/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/groundtransport/activity`,
        3145728
      )
    },
    Guestlist: {
      ExportXslx: endpoint(
        (guestlistId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/guestlist/export/xlsx/${guestlistId}`, { systemOneApiConfig }),
        (guestlistId: number) => `${baseURL}/guestlist/export/xlsx/${guestlistId}`,
        3145728
      ),
      UpdateGuestlistConfiguration: endpoint(
        (dto: server.dto.UpdateGuestlistConfigurationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/guestlist/updateGuestlistConfiguration`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/updateGuestlistConfiguration`,
        3145728
      ),
      CreateGuestlistConfiguration: endpoint(
        (dto: server.dto.CreateGuestlistConfigurationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/guestlist/createGuestlistConfiguration`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/createGuestlistConfiguration`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (guestlistId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.GuestlistSidePanel>>(`/guestlist/${guestlistId}/SidePanel`, { systemOneApiConfig }),
        (guestlistId: number) => `${baseURL}/guestlist/${guestlistId}/SidePanel`,
        3145728
      ),
      DeleteGuestlistConfiguration: endpoint(
        (dto: server.dto.DeleteGuestlistConfigurationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/guestlist/deleteGuestlistConfiguration`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/deleteGuestlistConfiguration`,
        3145728
      ),
      UpdateGuestlistAccessType: endpoint(
        (dto: server.dto.UpdateGuestlistAccessTypeInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/guestlist/updateGuestlistAccessType`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/updateGuestlistAccessType`,
        3145728
      ),
      CreateGuestlistAccessType: endpoint(
        (dto: server.dto.CreateGuestlistAccessTypeInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/guestlist/createGuestlistAccessType`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/createGuestlistAccessType`,
        3145728
      ),
      DeleteGuestlistAccessType: endpoint(
        (dto: server.dto.DeleteGuestlistAccessTypeInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/guestlist/deleteGuestlistAccessType`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/deleteGuestlistAccessType`,
        3145728
      ),
      GetGuest: endpoint(
        (dto: server.dto.GetGuestInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.Guest>>(`/guestlist/getGuest`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/getGuest`,
        3145728
      ),
      Paginated: endpoint(
        (dto: server.dto.PaginatedGuestInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.Guest[], server.dto.ListGuestFilters>>(`/guestlist/listGuest`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/listGuest`,
        3145728
      ),
      CreateGuest: endpoint(
        (dto: server.dto.CreateGuestInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/guestlist/createGuest`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/createGuest`,
        3145728
      ),
      UpdateGuest: endpoint(
        (dto: server.dto.UpdateGuestInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/guestlist/updateGuest`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/updateGuest`,
        3145728
      ),
      DeleteGuest: endpoint(
        (dto: server.dto.DeleteGuestInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/guestlist/deleteGuest`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/deleteGuest`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.GuestlistActivity[]>>(`/guestlist/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/guestlist/activity`,
        3145728
      )
    },
    HubSpot: {
      SearchCompanies: endpoint(
        (dto: server.dto.ListHubSpotCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.HubSpotCompany[], server.dto.ListHubSpotCompanyFilters>>(`/hubspot/company/search`, dto, { systemOneApiConfig }),
        () => `${baseURL}/hubspot/company/search`,
        3145728
      ),
      GetCompany: endpoint(
        (hubSpotCompanyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.HubSpotCompany>>(`/hubspot/company/${hubSpotCompanyId}`, { systemOneApiConfig }),
        (hubSpotCompanyId: number) => `${baseURL}/hubspot/company/${hubSpotCompanyId}`,
        3145728
      ),
      CreateCompany: endpoint(
        (dto: server.dto.CreateHubSpotCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/hubspot/company/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/hubspot/company/create`,
        3145728
      ),
      SyncCompanies: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/hubspot/maintain/SyncCompanies`, { systemOneApiConfig }),
        () => `${baseURL}/hubspot/maintain/SyncCompanies`,
        3145728
      ),
      SearchContacts: endpoint(
        (dto: server.dto.ListHubSpotContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.HubSpotContact[], server.dto.ListHubSpotContactFilters>>(`/hubspot/contact/search`, dto, { systemOneApiConfig }),
        () => `${baseURL}/hubspot/contact/search`,
        3145728
      ),
      GetContact: endpoint(
        (hubSpotContactId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.HubSpotContact>>(`/hubspot/contact/${hubSpotContactId}`, { systemOneApiConfig }),
        (hubSpotContactId: number) => `${baseURL}/hubspot/contact/${hubSpotContactId}`,
        3145728
      ),
      CreateContact: endpoint(
        (dto: server.dto.CreateHubSpotContactInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/hubspot/contact/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/hubspot/contact/create`,
        3145728
      ),
      SyncContacts: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/hubspot/maintain/SyncContacts`, { systemOneApiConfig }),
        () => `${baseURL}/hubspot/maintain/SyncContacts`,
        3145728
      ),
      SearchDeals: endpoint(
        (dto: server.dto.ListHubSpotDealInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.HubSpotDeal[], server.dto.ListHubSpotDealFilters>>(`/hubspot/deal/search`, dto, { systemOneApiConfig }),
        () => `${baseURL}/hubspot/deal/search`,
        3145728
      ),
      CreateDeal: endpoint(
        (dto: server.dto.CreateHubSpotDealInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/hubspot/deal/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/hubspot/deal/create`,
        3145728
      ),
      GetDeal: endpoint(
        (hubSpotDealId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.HubSpotDeal>>(`/hubspot/deal/${hubSpotDealId}`, { systemOneApiConfig }),
        (hubSpotDealId: number) => `${baseURL}/hubspot/deal/${hubSpotDealId}`,
        3145728
      ),
      SyncDeals: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/hubspot/maintain/SyncDeals`, { systemOneApiConfig }),
        () => `${baseURL}/hubspot/maintain/SyncDeals`,
        3145728
      )
    },
    ICalendarFeed: {
      List: endpoint(
        (dto: server.dto.ListICalendarFeedInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ICalendarFeedSmall[]>>(`/icalendarfeed/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendarfeed/list`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateICalendarFeedInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FeedRecordPeek>>(`/icalendarfeed/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendarfeed/update`,
        3145728
      ),
      CreateCalendarFeed: endpoint(
        (dto: server.dto.CreateICalendarFeedInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/icalendarfeed/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendarfeed/create`,
        3145728
      ),
      GetEventCount: endpoint(
        (iCalendarFeedId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<number>>(`/icalendarfeed/GetAppointmentCount`, { params: { iCalendarFeedId }, systemOneApiConfig }),
        (iCalendarFeedId: number) => `${baseURL}/icalendarfeed/GetAppointmentCount?iCalendarFeedId=${iCalendarFeedId}`,
        3145728
      ),
      Delete: endpoint(
        (iCalendarFeedId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/icalendarfeed/${iCalendarFeedId}/delete`, undefined, { systemOneApiConfig }),
        (iCalendarFeedId: number) => `${baseURL}/icalendarfeed/${iCalendarFeedId}/delete`,
        3145728
      ),
      EmailOverview: endpoint(
        (userId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/icalendarfeed/emailoverview`, { params: { userId }, systemOneApiConfig }),
        (userId: number) => `${baseURL}/icalendarfeed/emailoverview?userId=${userId}`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ICalendarFeedActivity[]>>(`/icalendarfeed/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendarfeed/activity`,
        3145728
      )
    },
    ICalendarTemplate: {
      List: endpoint(
        (dto: server.dto.PaginatedICalendarTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ICalendarTemplateSmall[], server.dto.ListICalendarTemplateFilters>>(`/icalendartemplate/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendartemplate/list`,
        3145728
      ),
      Copy: endpoint(
        (dto: server.dto.CopyICalendarTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/icalendartemplate/copy`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendartemplate/copy`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateICalendarTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/icalendartemplate/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendartemplate/create`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteICalendarTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/icalendartemplate/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendartemplate/delete`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateICalendarTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/icalendartemplate/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendartemplate/update`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ICalendarTemplateActivity[]>>(`/icalendartemplate/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/icalendartemplate/activity`,
        3145728
      )
    },
    Invoice: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedInvoiceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.InvoiceForList[], server.dto.ListInvoiceFilters>>(`/invoice/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.InvoiceV1SidePanelData>>(`/invoice/${invoiceId}/SidePanel`, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoice/${invoiceId}/SidePanel`,
        3145728
      ),
      ListInvoiceByShow: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.InvoiceForList[]>>(`/invoice/listByShow/${showId}`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/invoice/listByShow/${showId}`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateInvoiceV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/invoice/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/create`,
        3145728
      ),
      CreateInvoiceV1FromShow: endpoint(
        (dto: server.dto.CreateInvoiceV1FromShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/invoice/createFromShow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/createFromShow`,
        3145728
      ),
      Copy: endpoint(
        (data: server.dto.CopyInvoiceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/invoice/copy`, data, { systemOneApiConfig }),
        () => `${baseURL}/invoice/copy`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateInvoiceV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoice/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/update`,
        3145728
      ),
      MakeCredit: endpoint(
        (dto: server.dto.MakeCreditInvoiceV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/invoice/makecredit`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/makecredit`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteInvoiceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoice/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/delete`,
        3145728
      ),
      GetProcessedEmailTemplate: endpoint(
        (dto: server.dto.GetProcessedEmailTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ProcessedEmailTemplate>>(`/invoice/GetProcessedEmailTemplate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/GetProcessedEmailTemplate`,
        3145728
      ),
      ExportOptionsForList: endpoint(
        (dto: server.dto.PaginatedInvoiceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/invoice/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/ExportOptionsForList`,
        3145728
      ),
      ExportOptionsForDetail: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/invoice/ExportOptionsForDetail/${invoiceId}`, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoice/ExportOptionsForDetail/${invoiceId}`,
        3145728
      ),
      ExportItem: endpoint(
        (invoiceId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/item/${invoiceId}/${exportAction}/${clientFileName}`, { params: { wordTemplateId, preferredCulture }, systemOneApiConfig }),
        (invoiceId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID) => `${baseURL}/invoice/export/item/${invoiceId}/${exportAction}/${clientFileName}?wordTemplateId=${wordTemplateId}&preferredCulture=${preferredCulture}`,
        3145728
      ),
      ExportList: endpoint(
        (fileName: string, page: number, templateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/list/${fileName}`, { params: { page, templateId }, systemOneApiConfig }),
        (fileName: string, page: number, templateId: number) => `${baseURL}/invoice/export/list/${fileName}?page=${page}&templateId=${templateId}`,
        3145728
      ),
      ExportCustomListGeneral: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/list/custom/general/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/invoice/export/list/custom/general/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomInvoiceListSalesTax: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/list/custom/salestax/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/invoice/export/list/custom/salestax/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomInvoiceListForAllArtistAgency: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/list/custom/AllArtistAgency/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/invoice/export/list/custom/AllArtistAgency/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomInvoiceListForOctopusAgents_1: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/list/custom/octopusagents1/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/invoice/export/list/custom/octopusagents1/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomInvoiceListForOctopusAgents_2: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/list/custom/octopusagents2/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/invoice/export/list/custom/octopusagents2/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomInvoiceListForTemporarySecretary1: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/list/custom/temporarysecretary1/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/invoice/export/list/custom/temporarysecretary1/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomInvoiceListForTemporarySecretary2: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/list/custom/temporarysecretary2/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/invoice/export/list/custom/temporarysecretary2/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomInvoiceListForSeismic: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoice/export/list/custom/seismic/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/invoice/export/list/custom/seismic/${fileName}?page=${page}`,
        3145728
      ),
      ChargeByStripe: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/invoice/${invoiceId}/stripe-charge`, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoice/${invoiceId}/stripe-charge`,
        3145728
      ),
      SendSubscriptionInvoice: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/invoice/sendsubscriptioninvoice/${invoiceId}`, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoice/sendsubscriptioninvoice/${invoiceId}`,
        3145728
      ),
      GetAdminOnePanelData: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.AdminOneInvoicePanelData>>(`/invoice/${invoiceId}/adminone`, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoice/${invoiceId}/adminone`,
        3145728
      ),
      PaginatedFiles: endpoint(
        (dto: server.dto.PaginatedFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.File[]>>(`/invoice/listFiles`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/listFiles`,
        3145728
      ),
      PaginatedEmails: endpoint(
        (dto: server.dto.PaginatedEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.EmailMessage[]>>(`/invoice/listEmails`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/listEmails`,
        3145728
      ),
      ListRelatedFiles: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>(`/invoice/${invoiceId}/files/related`, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoice/${invoiceId}/files/related`,
        3145728
      ),
      LinkShows: endpoint(
        (dto: server.dto.LinkDocumentToShowsInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoice/shows/link`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/shows/link`,
        3145728
      ),
      UnlinkShow: endpoint(
        (dto: server.dto.UnlinkDocumentToShowInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoice/shows/unlink`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/shows/unlink`,
        3145728
      ),
      GetDocumentTotals: endpoint(
        (dto: server.dto.ListInvoiceTotalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.GroupedInvoiceTotals[], server.dto.ListInvoiceTotalFilters>>(`/invoice/stats/totalsdata`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoice/stats/totalsdata`,
        3145728
      )
    },
    InvoiceExport: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedInvoiceExportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.InvoiceExportMonth[], server.dto.ListInvoiceExportFilters>>(`/invoiceexport/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoiceexport/list`,
        3145728
      ),
      ConfigureInvoiceExport: endpoint(
        (dto: server.dto.ConfigureInvoiceExportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoiceexport/dataexport/config`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoiceexport/dataexport/config`,
        3145728
      ),
      GetExportConfiguration: endpoint(
        (dto: server.dto.GetInvoiceExportConfigurationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.InvoiceExportConfig>>(`/invoiceexport/getExportConfiguration`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoiceexport/getExportConfiguration`,
        3145728
      ),
      Export: endpoint(
        (bookkeepingId: number, year: number, month: number, type: server.dto.AvailableFileTypes, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoiceexport/export/bookkeeping`, { params: { bookkeepingId, year, month, type }, systemOneApiConfig }),
        (bookkeepingId: number, year: number, month: number, type: server.dto.AvailableFileTypes) => `${baseURL}/invoiceexport/export/bookkeeping?bookkeepingId=${bookkeepingId}&year=${year}&month=${month}&type=${type}`,
        3145728
      ),
      LockPerMonth: endpoint(
        (dto: server.dto.InvoiceExportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoiceexport/lockpermonth`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoiceexport/lockpermonth`,
        3145728
      )
    },
    InvoiceV2: {
      Create: endpoint(
        (dto: server.dto.CreateInvoiceV2Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/invoicev2/document/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/document/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateInvoiceV2Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/document/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/document/update`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.InvoiceV2SidePanelData>>(`/invoicev2/${invoiceId}/SidePanel`, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoicev2/${invoiceId}/SidePanel`,
        3145728
      ),
      MakeCredit: endpoint(
        (dto: server.dto.MakeCreditInvoiceV2Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/invoicev2/makecredit`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/makecredit`,
        3145728
      ),
      ChangeToDraftStage: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/document/${invoiceId}/changeToDraftStage`, undefined, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoicev2/document/${invoiceId}/changeToDraftStage`,
        3145728
      ),
      ChangeToReviewStage: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/document/${invoiceId}/changeToReviewStage`, undefined, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoicev2/document/${invoiceId}/changeToReviewStage`,
        3145728
      ),
      GetPreChangeToOutstandingInfo: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.PreChangeToOutstandingInfo>>(`/invoicev2/document/${invoiceId}/GetPreChangeToOutstandingInfo`, undefined, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoicev2/document/${invoiceId}/GetPreChangeToOutstandingInfo`,
        3145728
      ),
      ChangeToOutstanding: endpoint(
        (dto: server.dto.ChangeInvoiceV2ToOutstandingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/document/changeToOutstandingStage`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/document/changeToOutstandingStage`,
        3145728
      ),
      DownloadPdf: endpoint(
        (invoiceId: number, fileName: string, inline: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoicev2/document/${invoiceId}/download/pdf/${fileName}`, { params: { inline }, systemOneApiConfig }),
        (invoiceId: number, fileName: string, inline: boolean) => `${baseURL}/invoicev2/document/${invoiceId}/download/pdf/${fileName}?inline=${inline}`,
        3145728
      ),
      PreviewDocument: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoicev2/preview/${invoiceId}`, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoicev2/preview/${invoiceId}`,
        3145728
      ),
      AccountingSoftwareResend: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/AccountingSoftwareResend/${invoiceId}`, undefined, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoicev2/AccountingSoftwareResend/${invoiceId}`,
        3145728
      ),
      ListParticipants: endpoint(
        (dto: server.dto.ListInvoiceParticipantInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.InvoiceParticipant[]>>(`/invoicev2/listParticipants`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/listParticipants`,
        3145728
      ),
      AddParticipant: endpoint(
        (dto: server.dto.AddInvoiceParticipantInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/invoicev2/addParticipant`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/addParticipant`,
        3145728
      ),
      UpdateParticipant: endpoint(
        (dto: server.dto.UpdateInvoiceParticipantInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/updateParticipant`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/updateParticipant`,
        3145728
      ),
      DeleteParticipant: endpoint(
        (dto: server.dto.DeleteInvoiceParticipantInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/deleteParticipant`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/deleteParticipant`,
        3145728
      ),
      ResendParticipantEmail: endpoint(
        (dto: server.dto.ResendParticipantEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/resendParticipantEmail`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/resendParticipantEmail`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.InvoiceActivity[]>>(`/invoicev2/document/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/document/activity`,
        3145728
      ),
      ListReminders: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.InvoiceReminder[]>>(`/invoicev2/document/${invoiceId}/reminder`, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoicev2/document/${invoiceId}/reminder`,
        3145728
      ),
      DeleteAllReminders: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/document/${invoiceId}/reminder/clear/all`, undefined, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoicev2/document/${invoiceId}/reminder/clear/all`,
        3145728
      ),
      DeleteLastReminder: endpoint(
        (invoiceId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/document/${invoiceId}/reminder/clear/last`, undefined, { systemOneApiConfig }),
        (invoiceId: number) => `${baseURL}/invoicev2/document/${invoiceId}/reminder/clear/last`,
        3145728
      ),
      AddReminder: endpoint(
        (dto: server.dto.AddInvoiceReminderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/reminder/add`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/reminder/add`,
        3145728
      ),
      UpdateReminder: endpoint(
        (dto: server.dto.UpdateInvoiceReminderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/invoicev2/reminder/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/invoicev2/reminder/update`,
        3145728
      )
    },
    InvoiceV2Open: {
      DownloadPdfBySecret: endpoint(
        (secret: string, fileName: string, inline: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/invoicev2/open/participant/${secret}/download/pdf/${fileName}`, { params: { inline }, systemOneApiConfig }),
        (secret: string, fileName: string, inline: boolean) => `${baseURL}/invoicev2/open/participant/${secret}/download/pdf/${fileName}?inline=${inline}`,
        3145728
      ),
      PreviewDocument: endpoint(
        (secret: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/invoicev2/open/preview/${secret}`, { systemOneApiConfig }),
        (secret: string) => `${baseURL}/invoicev2/open/preview/${secret}`,
        3145728
      )
    },
    ItemAccess: {
      PaginatedUserAccessToArtist: endpoint(
        (dto: server.dto.ListUserByItemAccessInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.UserAccessToArtist[]>>(`/itemaccess/PaginatedUserAccessToArtist`, dto, { systemOneApiConfig }),
        () => `${baseURL}/itemaccess/PaginatedUserAccessToArtist`,
        3145728
      )
    },
    Itinerary: {
      Export: endpoint(
        (fileName: string, dto: server.dto.ExportItineraryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/itinerary/export/${fileName}`, dto, { systemOneApiConfig }),
        (fileName: string) => `${baseURL}/itinerary/export/${fileName}`,
        3145728
      ),
      Count: endpoint(
        (dto: server.dto.ExportItineraryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/itinerary/count`, dto, { systemOneApiConfig }),
        () => `${baseURL}/itinerary/count`,
        3145728
      )
    },
    MergeField: {
      ListAvailable: endpoint(
        (dto: server.dto.ListAvailableMergeFieldInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.AvailableMergeFieldTopic[]>>(`/mergefield/available`, dto, { systemOneApiConfig }),
        () => `${baseURL}/mergefield/available`,
        3145728
      )
    },
    MessageTemplate: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedMessageTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.MessageTemplate[], server.dto.ListMessageTemplateFilters>>(`/messagetemplate/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/list`,
        3145728
      ),
      GetItem: endpoint(
        (messageTemplateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.MessageTemplate>>(`/messagetemplate/${messageTemplateId}`, { systemOneApiConfig }),
        (messageTemplateId: number) => `${baseURL}/messagetemplate/${messageTemplateId}`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateMessageTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/messagetemplate/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/create`,
        3145728
      ),
      Copy: endpoint(
        (dto: server.dto.CopyMessageTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/messagetemplate/copy`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/copy`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateMessageTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/update`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteMessageTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/delete`,
        3145728
      ),
      CreateEmailTranslation: endpoint(
        (dto: server.dto.CreateMessageTemplateTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/createEmailTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/createEmailTranslation`,
        3145728
      ),
      SetEmailTranslation: endpoint(
        (dto: server.dto.SetMessageTemplate_EmailTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/setEmailTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/setEmailTranslation`,
        3145728
      ),
      ListEmailTranslation: endpoint(
        (messageTemplateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.MessageTemplate_EmailTranslation[]>>(`/messagetemplate/listEmailTranslation/${messageTemplateId}`, { systemOneApiConfig }),
        (messageTemplateId: number) => `${baseURL}/messagetemplate/listEmailTranslation/${messageTemplateId}`,
        3145728
      ),
      PreferredEmailTemplateTranslation: endpoint(
        (dto: server.dto.GetPreferredMessageTemplateTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.MessageTemplate_EmailTranslation>>(`/messagetemplate/preferredEmailTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/preferredEmailTranslation`,
        3145728
      ),
      CreateInvitationTranslation: endpoint(
        (dto: server.dto.CreateMessageTemplateTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/createInvitationTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/createInvitationTranslation`,
        3145728
      ),
      SetInvitationTranslation: endpoint(
        (dto: server.dto.SetMessageTemplate_InvitationTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/setInvitationTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/setInvitationTranslation`,
        3145728
      ),
      ListInvitationTranslation: endpoint(
        (messageTemplateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.MessageTemplate_InvitationTranslation[]>>(`/messagetemplate/listInvitationTranslation/${messageTemplateId}`, { systemOneApiConfig }),
        (messageTemplateId: number) => `${baseURL}/messagetemplate/listInvitationTranslation/${messageTemplateId}`,
        3145728
      ),
      PreferredInvitationTemplateTranslation: endpoint(
        (dto: server.dto.GetPreferredMessageTemplateTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.MessageTemplate_InvitationTranslation>>(`/messagetemplate/preferredInvitationTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/preferredInvitationTranslation`,
        3145728
      ),
      CreateInvoiceDocumentTranslation: endpoint(
        (dto: server.dto.CreateMessageTemplateTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/createInvoiceDocumentTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/createInvoiceDocumentTranslation`,
        3145728
      ),
      SetInvoiceDocumentTranslation: endpoint(
        (dto: server.dto.SetMessageTemplate_InvoiceDocumentTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/setInvoiceDocumentTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/setInvoiceDocumentTranslation`,
        3145728
      ),
      ListInvoiceDocumentTranslation: endpoint(
        (messageTemplateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.MessageTemplate_InvoiceDocumentTranslation[]>>(`/messagetemplate/listInvoiceDocumentTranslation/${messageTemplateId}`, { systemOneApiConfig }),
        (messageTemplateId: number) => `${baseURL}/messagetemplate/listInvoiceDocumentTranslation/${messageTemplateId}`,
        3145728
      ),
      PreferredInvoiceDocumentTemplateTranslation: endpoint(
        (dto: server.dto.GetPreferredMessageTemplateTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.MessageTemplate_InvoiceDocumentTranslation>>(`/messagetemplate/preferredInvoiceDocumentTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/preferredInvoiceDocumentTranslation`,
        3145728
      ),
      CreateReminderTranslation: endpoint(
        (dto: server.dto.CreateMessageTemplateTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/createReminderTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/createReminderTranslation`,
        3145728
      ),
      SetReminderTranslation: endpoint(
        (dto: server.dto.SetMessageTemplate_ReminderTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/setReminderTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/setReminderTranslation`,
        3145728
      ),
      ListReminderTranslation: endpoint(
        (messageTemplateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.MessageTemplate_ReminderTranslation[]>>(`/messagetemplate/listReminderTranslation/${messageTemplateId}`, { systemOneApiConfig }),
        (messageTemplateId: number) => `${baseURL}/messagetemplate/listReminderTranslation/${messageTemplateId}`,
        3145728
      ),
      PreferredReminderTemplateTranslation: endpoint(
        (dto: server.dto.GetPreferredMessageTemplateTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.MessageTemplate_ReminderTranslation>>(`/messagetemplate/preferredReminderTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/preferredReminderTranslation`,
        3145728
      ),
      DeleteTranslation: endpoint(
        (dto: server.dto.DeleteMessageTemplateTranslationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/messagetemplate/deleteTranslation`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/deleteTranslation`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.MessageTemplateActivity[]>>(`/messagetemplate/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/messagetemplate/activity`,
        3145728
      )
    },
    MySelection: {
      Add: endpoint(
        (instruction: server.dto.AddToMySelectionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/myselection/add`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/myselection/add`,
        3145728
      ),
      Remove: endpoint(
        (instruction: server.dto.RemoveFromMySelectionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/myselection/remove`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/myselection/remove`,
        3145728
      ),
      ClearAll: endpoint(
        (mainArea: server.dto.MainAreas, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/myselection/clearall`, undefined, { params: { mainArea }, systemOneApiConfig }),
        (mainArea: server.dto.MainAreas) => `${baseURL}/myselection/clearall?mainArea=${mainArea}`,
        3145728
      ),
      Count: endpoint(
        (mainArea: server.dto.MainAreas, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<number>>(`/myselection/count`, { params: { mainArea }, systemOneApiConfig }),
        (mainArea: server.dto.MainAreas) => `${baseURL}/myselection/count?mainArea=${mainArea}`,
        3145728
      ),
      Warning: endpoint(
        (dto: server.dto.FetchWarningForMySelectionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/myselection/warning`, dto, { systemOneApiConfig }),
        () => `${baseURL}/myselection/warning`,
        3145728
      ),
      ExportMyClientFileSelectionInZip: endpoint(
        (fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/myselection/export/${fileName}`, { systemOneApiConfig }),
        (fileName: string) => `${baseURL}/myselection/export/${fileName}`,
        3145728
      ),
      BatchChangeTags: endpoint(
        (instruction: server.dto.BatchChangeTagsForMySelectionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/myselection/tags`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/myselection/tags`,
        3145728
      ),
      BatchChangeOwner: endpoint(
        (instruction: server.dto.BatchSetOwnerForMySelectionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/myselection/owner`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/myselection/owner`,
        3145728
      ),
      BatchSetPublication: endpoint(
        (instruction: server.dto.BatchSetShowPublicationForMySelectionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/myselection/publication`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/myselection/publication`,
        3145728
      ),
      BatchChangeStatus: endpoint(
        (instruction: server.dto.BatchSetStatusForMySelectionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/myselection/status`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/myselection/status`,
        3145728
      ),
      BatchDelete: endpoint(
        (instruction: server.dto.BatchDeleteForMySelectionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/myselection/delete`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/myselection/delete`,
        3145728
      ),
      BatchChangeVisibleToExternal: endpoint(
        (instruction: server.dto.BatchSetVisibleToExternalForMySelectionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/myselection/visibletoexternal`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/myselection/visibletoexternal`,
        3145728
      )
    },
    NorwegianCompanyRegister: {
      TextSearch: endpoint(
        (dto: server.dto.SearchNorwegianCompanyRegisterInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.NorwegianCompanyRegistration[]>>(`/norwegiancompanyregister/textsearch`, dto, { systemOneApiConfig }),
        () => `${baseURL}/norwegiancompanyregister/textsearch`,
        3145728
      )
    },
    Note: {
      List: endpoint(
        (instruction: server.dto.ListNoteInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.Note[]>>(`/note/list`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/note/list`,
        3145728
      ),
      CreateNote: endpoint(
        (instruction: server.dto.CreateNoteInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponse>(`/note/create`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/note/create`,
        3145728
      ),
      DeleteNote: endpoint(
        (noteId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/note/${noteId}`, { systemOneApiConfig }),
        (noteId: number) => `${baseURL}/note/${noteId}`,
        3145728
      ),
      UpdateNote: endpoint(
        (instruction: server.dto.UpdateNoteInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/note/update`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/note/update`,
        3145728
      )
    },
    Notification: {
      Paginated: endpoint(
        (dto: server.dto.ListNotificationInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.Notification[], server.dto.ListNotificationFilters>>(`/notification/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/notification/list`,
        3145728
      ),
      MarkRead: endpoint(
        (notifications: number[], read: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/notification/read/${read}`, notifications, { systemOneApiConfig }),
        (read: boolean) => `${baseURL}/notification/read/${read}`,
        3145728
      ),
      UpdateSettings: endpoint(
        (settings: server.dto.NotificationSettings, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.NotificationSettings>>(`/notification/settings`, settings, { systemOneApiConfig }),
        () => `${baseURL}/notification/settings`,
        3145728
      ),
      Hide: endpoint(
        (notifications: number[], systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/notification/hide`, notifications, { systemOneApiConfig }),
        () => `${baseURL}/notification/hide`,
        3145728
      ),
      MarkAllRead: endpoint(
        (channel: server.dto.NotificationChannels, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/notification/allread`, undefined, { params: { channel }, systemOneApiConfig }),
        (channel: server.dto.NotificationChannels) => `${baseURL}/notification/allread?channel=${channel}`,
        3145728
      )
    },
    Payment: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedPaymentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.PaymentForList[], server.dto.ListPaymentFilters>>(`/payment/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/payment/list`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreatePaymentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapper<number>>(`/payment/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/payment/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdatePaymentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/payment/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/payment/update`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeletePaymentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/payment/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/payment/delete`,
        3145728
      ),
      ListByInvoice: endpoint(
        (dto: server.dto.ListPaymentByInvoiceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.PaymentsForInvoice>>(`/payment/ListByInvoice`, dto, { systemOneApiConfig }),
        () => `${baseURL}/payment/ListByInvoice`,
        3145728
      ),
      ExportPaymentList: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/payment/export/list/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/payment/export/list/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomPaymentListForTemporarySecretary: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/payment/export/list/custom/temporarysecretary/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/payment/export/list/custom/temporarysecretary/${fileName}?page=${page}`,
        3145728
      ),
      ExportOptionsForList: endpoint(
        (dto: server.dto.PaginatedPaymentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/payment/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/payment/ExportOptionsForList`,
        3145728
      )
    },
    PaymentTerm: {
      ListOptions: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.PaymentTermOption[]>>(`/paymentterm/option/list`, { systemOneApiConfig }),
        () => `${baseURL}/paymentterm/option/list`,
        3145728
      )
    },
    Peppol: {
      RegisterBookkeeping: endpoint(
        (dto: server.dto.EInvoiceRegisterBookkeepingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/peppol/RegisterBookkeeping`, dto, { systemOneApiConfig }),
        () => `${baseURL}/peppol/RegisterBookkeeping`,
        3145728
      ),
      SubmitInvoice: endpoint(
        (invoiceId: number, resend: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/peppol/SubmitInvoice/${invoiceId}`, undefined, { params: { resend }, systemOneApiConfig }),
        (invoiceId: number, resend: boolean) => `${baseURL}/peppol/SubmitInvoice/${invoiceId}?resend=${resend}`,
        3145728
      )
    },
    Planning: {
      PlanningByDay: endpoint(
        (dto: server.dto.PlanningByDayQuery, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.AppointmentForPlanner[], server.dto.PlanningByDayFilters>>(`/planning/PlanningByDay`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planning/PlanningByDay`,
        3145728
      ),
      PlanningByMonth: endpoint(
        (dto: server.dto.PlanningByMonthQuery, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.AppointmentsDayGroup[], server.dto.PlanningByMonthFilters>>(`/planning/PlanningByMonth`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planning/PlanningByMonth`,
        3145728
      ),
      PlanningByQuarter: endpoint(
        (dto: server.dto.PlanningByQuarterQuery, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.PlannerDay[], server.dto.PlanningByQuarterFilters>>(`/planning/PlanningByQuarter`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planning/PlanningByQuarter`,
        3145728
      ),
      PlanningByQuarterExportOptions: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/planning/PlanningByQuarter/ExportOptions`, undefined, { systemOneApiConfig }),
        () => `${baseURL}/planning/PlanningByQuarter/ExportOptions`,
        3145728
      ),
      ExportQuarterlyPlanning: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/planning/PlanningByQuarter/export/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/planning/PlanningByQuarter/export/${fileName}?page=${page}`,
        3145728
      ),
      PlanningByWeek: endpoint(
        (dto: server.dto.PlanningByWeekQuery, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.AppointmentsDayGroup[], server.dto.PlanningByWeekFilters>>(`/planning/PlanningByWeek`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planning/PlanningByWeek`,
        3145728
      ),
      CountAppointmentPerMonth: endpoint(
        (dto: server.dto.CountAppointmentPerMonthInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.AppointmentCount[]>>(`/planning/CountAppointmentPerMonth`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planning/CountAppointmentPerMonth`,
        3145728
      ),
      PaginatedAppointment: endpoint(
        (dto: server.dto.PaginatedAppointmentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.PaginatedAppointmentResult>>(`/planning/PaginatedAppointment`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planning/PaginatedAppointment`,
        3145728
      ),
      PlanningForItinerary: endpoint(
        (dto: server.dto.PlanningForItineraryQuery, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.PlannerDay[], server.dto.PlanningForItineraryFilters>>(`/planning/PlanningForItinerary`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planning/PlanningForItinerary`,
        3145728
      )
    },
    PlanningMap: {
      PaginatedShow: endpoint(
        (dto: server.dto.PaginatedShowInternalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ShowForPlanningMap[], server.dto.ListShowInternalFilters>>(`/planningmap/show`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planningmap/show`,
        3145728
      ),
      PaginatedVenue: endpoint(
        (dto: server.dto.PaginatedVenueInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.VenueSmall[], server.dto.ListVenueFilters>>(`/planningmap/venue`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planningmap/venue`,
        3145728
      ),
      PaginatedAirport: endpoint(
        (dto: server.dto.PaginatedAirportInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.Airport[], server.dto.ListAirportFilters>>(`/planningmap/airport`, dto, { systemOneApiConfig }),
        () => `${baseURL}/planningmap/airport`,
        3145728
      )
    },
    Product: {
      ListByParent: endpoint(
        (dto: server.dto.ListProductByParentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ProductsByParent>>(`/product/listbyparent`, dto, { systemOneApiConfig }),
        () => `${baseURL}/product/listbyparent`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateProductInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/product/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/product/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateProductInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/product/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/product/update`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteProductInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/product/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/product/delete`,
        3145728
      ),
      StoreSorted: endpoint(
        (dto: server.dto.StoreSortedProductInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/product/sort`, dto, { systemOneApiConfig }),
        () => `${baseURL}/product/sort`,
        3145728
      )
    },
    ProductGroup: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedProductGroupInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ProductGroupForList[], server.dto.ListProductGroupFilters>>(`/productgroup/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/productgroup/list`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateProductGroupInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/productgroup/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/productgroup/update`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateProductGroupInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/productgroup/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/productgroup/create`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteProductGroupInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/productgroup/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/productgroup/delete`,
        3145728
      )
    },
    Production: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedProductionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.TourForList[], server.dto.ListProductionFilters>>(`/production/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/production/list`,
        3145728
      ),
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedProductionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.TourSmall[], server.dto.ListProductionFilters>>(`/production/listSmall`, dto, { systemOneApiConfig }),
        () => `${baseURL}/production/listSmall`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (productionId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.TourSidePanelData>>(`/production/${productionId}/SidePanel`, { systemOneApiConfig }),
        (productionId: number) => `${baseURL}/production/${productionId}/SidePanel`,
        3145728
      ),
      Copy: endpoint(
        (productionId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/production/copy`, undefined, { params: { productionId }, systemOneApiConfig }),
        (productionId: number) => `${baseURL}/production/copy?productionId=${productionId}`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateProductionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/production/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/production/create`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteProductionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/production/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/production/delete`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateTourInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/production/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/production/update`,
        3145728
      ),
      UpdateFreeField: endpoint(
        (dto: server.dto.UpdateFreeFieldValueInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/production/updateFreeField`, dto, { systemOneApiConfig }),
        () => `${baseURL}/production/updateFreeField`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.TourActivity[]>>(`/production/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/production/activity`,
        3145728
      ),
      ExportList: endpoint(
        (fileName: string, page: number, templateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/production/export/list/${fileName}`, { params: { page, templateId }, systemOneApiConfig }),
        (fileName: string, page: number, templateId: number) => `${baseURL}/production/export/list/${fileName}?page=${page}&templateId=${templateId}`,
        3145728
      ),
      ExportOptionsForList: endpoint(
        (dto: server.dto.PaginatedProductionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/production/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/production/ExportOptionsForList`,
        3145728
      )
    },
    ProductTemplates: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedProductTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ProductTemplate[], server.dto.ListProductTemplateFilters>>(`/producttemplates/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/producttemplates/list`,
        3145728
      ),
      Copy: endpoint(
        (dto: server.dto.CopyProductTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/producttemplates/copy`, dto, { systemOneApiConfig }),
        () => `${baseURL}/producttemplates/copy`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteProductTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/producttemplates/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/producttemplates/delete`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateProductTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/producttemplates/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/producttemplates/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateProductTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/producttemplates/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/producttemplates/update`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ProductTemplateActivity[]>>(`/producttemplates/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/producttemplates/activity`,
        3145728
      )
    },
    Promoter: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedPromoterInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.PromoterForList[], server.dto.ListPromoterFilters>>(`/promoter/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/promoter/list`,
        3145728
      ),
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedPromoterInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.CompanySmall[], server.dto.ListPromoterFilters>>(`/promoter/listSmall`, dto, { systemOneApiConfig }),
        () => `${baseURL}/promoter/listSmall`,
        3145728
      )
    },
    PurchaseAgreement: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.DocumentForList[], server.dto.ListDocumentFilters>>(`/purchaseagreement/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.DocumentV1SidePanelData>>(`/purchaseagreement/${documentId}/SidePanel`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/purchaseagreement/${documentId}/SidePanel`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateDocumentV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/purchaseagreement/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/create`,
        3145728
      ),
      CreateDocumentV1FromShow: endpoint(
        (dto: server.dto.CreateDocumentV1FromShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/purchaseagreement/createFromShow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/createFromShow`,
        3145728
      ),
      Copy: endpoint(
        (dto: server.dto.CopyDocumentV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/purchaseagreement/copy`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/copy`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateDocumentV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/purchaseagreement/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/update`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/purchaseagreement/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/delete`,
        3145728
      ),
      GetProcessedEmailTemplate: endpoint(
        (dto: server.dto.GetProcessedEmailTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ProcessedEmailTemplate>>(`/purchaseagreement/GetProcessedEmailTemplate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/GetProcessedEmailTemplate`,
        3145728
      ),
      ExportOptionsForList: endpoint(
        (dto: server.dto.PaginatedDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/purchaseagreement/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/ExportOptionsForList`,
        3145728
      ),
      LinkShows: endpoint(
        (dto: server.dto.LinkDocumentToShowsInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/purchaseagreement/shows/link`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/shows/link`,
        3145728
      ),
      UnlinkShow: endpoint(
        (dto: server.dto.UnlinkDocumentToShowInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/purchaseagreement/shows/unlink`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/shows/unlink`,
        3145728
      ),
      PaginatedFiles: endpoint(
        (dto: server.dto.PaginatedFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.File[]>>(`/purchaseagreement/listFiles`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/listFiles`,
        3145728
      ),
      PaginatedEmails: endpoint(
        (dto: server.dto.PaginatedEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.EmailMessage[]>>(`/purchaseagreement/listEmails`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/listEmails`,
        3145728
      ),
      ListRelatedFiles: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>(`/purchaseagreement/${documentId}/files/related`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/purchaseagreement/${documentId}/files/related`,
        3145728
      ),
      ExportItem: endpoint(
        (documentId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/purchaseagreement/export/item/${documentId}/${exportAction}/${clientFileName}`, { params: { wordTemplateId, preferredCulture }, systemOneApiConfig }),
        (documentId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID) => `${baseURL}/purchaseagreement/export/item/${documentId}/${exportAction}/${clientFileName}?wordTemplateId=${wordTemplateId}&preferredCulture=${preferredCulture}`,
        3145728
      ),
      ExportCustomListGeneral: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/purchaseagreement/export/list/custom/general/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/purchaseagreement/export/list/custom/general/${fileName}?page=${page}`,
        3145728
      ),
      ExportList: endpoint(
        (fileName: string, page: number, templateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/purchaseagreement/export/list/${fileName}`, { params: { page, templateId }, systemOneApiConfig }),
        (fileName: string, page: number, templateId: number) => `${baseURL}/purchaseagreement/export/list/${fileName}?page=${page}&templateId=${templateId}`,
        3145728
      ),
      ExportOptionsForDetail: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/purchaseagreement/ExportOptionsForDetail/${documentId}`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/purchaseagreement/ExportOptionsForDetail/${documentId}`,
        3145728
      ),
      GetDocumentTotals: endpoint(
        (dto: server.dto.ListDocumentTotalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.GroupedDocumentTotals[], server.dto.ListDocumentTotalFilters>>(`/purchaseagreement/stats/totalsdata`, dto, { systemOneApiConfig }),
        () => `${baseURL}/purchaseagreement/stats/totalsdata`,
        3145728
      )
    },
    Quote: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.DocumentForList[], server.dto.ListDocumentFilters>>(`/quote/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.DocumentV1SidePanelData>>(`/quote/${documentId}/SidePanel`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/quote/${documentId}/SidePanel`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateDocumentV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/quote/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/create`,
        3145728
      ),
      CreateDocumentV1FromShow: endpoint(
        (dto: server.dto.CreateDocumentV1FromShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/quote/createFromShow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/createFromShow`,
        3145728
      ),
      Copy: endpoint(
        (dto: server.dto.CopyDocumentV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/quote/copy`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/copy`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateDocumentV1Instruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/quote/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/update`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/quote/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/delete`,
        3145728
      ),
      GetProcessedEmailTemplate: endpoint(
        (dto: server.dto.GetProcessedEmailTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ProcessedEmailTemplate>>(`/quote/GetProcessedEmailTemplate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/GetProcessedEmailTemplate`,
        3145728
      ),
      ExportOptionsForList: endpoint(
        (dto: server.dto.PaginatedDocumentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/quote/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/ExportOptionsForList`,
        3145728
      ),
      LinkShows: endpoint(
        (dto: server.dto.LinkDocumentToShowsInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/quote/shows/link`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/shows/link`,
        3145728
      ),
      UnlinkShow: endpoint(
        (dto: server.dto.UnlinkDocumentToShowInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/quote/shows/unlink`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/shows/unlink`,
        3145728
      ),
      PaginatedFiles: endpoint(
        (dto: server.dto.PaginatedFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.File[]>>(`/quote/listFiles`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/listFiles`,
        3145728
      ),
      PaginatedEmails: endpoint(
        (dto: server.dto.PaginatedEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.EmailMessage[]>>(`/quote/listEmails`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/listEmails`,
        3145728
      ),
      ListRelatedFiles: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>(`/quote/${documentId}/files/related`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/quote/${documentId}/files/related`,
        3145728
      ),
      ExportItem: endpoint(
        (documentId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/quote/export/item/${documentId}/${exportAction}/${clientFileName}`, { params: { wordTemplateId, preferredCulture }, systemOneApiConfig }),
        (documentId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID) => `${baseURL}/quote/export/item/${documentId}/${exportAction}/${clientFileName}?wordTemplateId=${wordTemplateId}&preferredCulture=${preferredCulture}`,
        3145728
      ),
      ExportCustomListGeneral: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/quote/export/list/custom/general/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/quote/export/list/custom/general/${fileName}?page=${page}`,
        3145728
      ),
      ExportList: endpoint(
        (fileName: string, page: number, templateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/quote/export/list/${fileName}`, { params: { page, templateId }, systemOneApiConfig }),
        (fileName: string, page: number, templateId: number) => `${baseURL}/quote/export/list/${fileName}?page=${page}&templateId=${templateId}`,
        3145728
      ),
      ExportOptionsForDetail: endpoint(
        (documentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/quote/ExportOptionsForDetail/${documentId}`, { systemOneApiConfig }),
        (documentId: number) => `${baseURL}/quote/ExportOptionsForDetail/${documentId}`,
        3145728
      ),
      GetDocumentTotals: endpoint(
        (dto: server.dto.ListDocumentTotalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.GroupedDocumentTotals[], server.dto.ListDocumentTotalFilters>>(`/quote/stats/totalsdata`, dto, { systemOneApiConfig }),
        () => `${baseURL}/quote/stats/totalsdata`,
        3145728
      )
    },
    Recurring: {
      DataHousekeeping: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/DataHousekeeping`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/DataHousekeeping`,
        3145728
      ),
      ESignProcessSignAutomatically: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/ESignProcessSignAutomatically`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/ESignProcessSignAutomatically`,
        3145728
      ),
      EndTemporaryClientAccess: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/EndTemporaryClientAccess`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/EndTemporaryClientAccess`,
        3145728
      ),
      ProcessGeoCode: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/ProcessGeoCode`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/ProcessGeoCode`,
        3145728
      ),
      ProcessVatNumbers: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/ProcessVatNumbers`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/ProcessVatNumbers`,
        3145728
      ),
      RenewUpcomingSubscriptions: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/RenewUpcomingSubscriptions`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/RenewUpcomingSubscriptions`,
        3145728
      ),
      SendUserTaskReminderEmails: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/SendUserTaskReminderEmails`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/SendUserTaskReminderEmails`,
        3145728
      ),
      SendDocumentReminderEmails: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/SendDocumentReminderEmails`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/SendDocumentReminderEmails`,
        3145728
      ),
      SendInvoiceReminderEmails: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/SendInvoiceReminderEmails`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/SendInvoiceReminderEmails`,
        3145728
      ),
      SyncAirlinesFromFlightStats: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/SyncAirlinesFromFlightStats`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/SyncAirlinesFromFlightStats`,
        3145728
      ),
      SyncAirportsFromFlightStats: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/SyncAirportsFromFlightStats`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/SyncAirportsFromFlightStats`,
        3145728
      ),
      SyncExchangeRatesFromFixer: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/SyncExchangeRatesFromFixer`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/SyncExchangeRatesFromFixer`,
        3145728
      ),
      SyncUserLastActivity: endpoint(
        (withinMinutes: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/SyncUserLastActivity`, { params: { withinMinutes }, systemOneApiConfig }),
        (withinMinutes: number) => `${baseURL}/recurring/SyncUserLastActivity?withinMinutes=${withinMinutes}`,
        3145728
      ),
      ProcessExtractorSessions: endpoint(
        (maxFailCount: number, take: number, daysHistory: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/ProcessExtractorSessions`, { params: { maxFailCount, take, daysHistory }, systemOneApiConfig }),
        (maxFailCount: number, take: number, daysHistory: number) => `${baseURL}/recurring/ProcessExtractorSessions?maxFailCount=${maxFailCount}&take=${take}&daysHistory=${daysHistory}`,
        3145728
      ),
      ExpireCredits: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/ExpireCredits`, { systemOneApiConfig }),
        () => `${baseURL}/recurring/ExpireCredits`,
        3145728
      ),
      GenerateMissingInvoicePdf: endpoint(
        (maxFailCount: number, take: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/GenerateMissingInvoicePdf`, { params: { maxFailCount, take }, systemOneApiConfig }),
        (maxFailCount: number, take: number) => `${baseURL}/recurring/GenerateMissingInvoicePdf?maxFailCount=${maxFailCount}&take=${take}`,
        3145728
      ),
      GenerateMissingEN16931: endpoint(
        (maxFailCount: number, take: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/GenerateMissingEN16931`, { params: { maxFailCount, take }, systemOneApiConfig }),
        (maxFailCount: number, take: number) => `${baseURL}/recurring/GenerateMissingEN16931?maxFailCount=${maxFailCount}&take=${take}`,
        3145728
      ),
      GenerateMissingPeppolBis3: endpoint(
        (maxFailCount: number, take: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/GenerateMissingPeppolBis3`, { params: { maxFailCount, take }, systemOneApiConfig }),
        (maxFailCount: number, take: number) => `${baseURL}/recurring/GenerateMissingPeppolBis3?maxFailCount=${maxFailCount}&take=${take}`,
        3145728
      ),
      SubmitPeppolInvoice: endpoint(
        (maxFailCount: number, take: number, daysHistory: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/SubmitPeppolInvoice`, { params: { maxFailCount, take, daysHistory }, systemOneApiConfig }),
        (maxFailCount: number, take: number, daysHistory: number) => `${baseURL}/recurring/SubmitPeppolInvoice?maxFailCount=${maxFailCount}&take=${take}&daysHistory=${daysHistory}`,
        3145728
      ),
      EmailAccountingSoftware: endpoint(
        (maxFailCount: number, take: number, daysHistory: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/recurring/AccountingSoftwareSend`, { params: { maxFailCount, take, daysHistory }, systemOneApiConfig }),
        (maxFailCount: number, take: number, daysHistory: number) => `${baseURL}/recurring/AccountingSoftwareSend?maxFailCount=${maxFailCount}&take=${take}&daysHistory=${daysHistory}`,
        3145728
      )
    },
    Register: {
      SelfRegisterFromInvitation: endpoint(
        (dto: server.dto.SelfRegisterInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.SelfRegisterOutcome>(`/register`, dto, { systemOneApiConfig }),
        () => `${baseURL}/register`,
        3145728
      ),
      ValidateUsername: endpoint(
        (username: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.UsernameValidationResult>(`/register/usernameexists/${username}`, { systemOneApiConfig }),
        (username: string) => `${baseURL}/register/usernameexists/${username}`,
        3145728
      )
    },
    Show: {
      GetShow: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.Show>>(`/show/${showId}`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/show/${showId}`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ShowSidePanelData>>(`/show/${showId}/SidePanel`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/show/${showId}/SidePanel`,
        3145728
      ),
      PaginatedInternalEnvironment: endpoint(
        (dto: server.dto.PaginatedShowInternalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ShowForListInternal[], server.dto.ListShowInternalFilters>>(`/show/internalenvironment/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/internalenvironment/list`,
        3145728
      ),
      SumInternalEnvironment: endpoint(
        (dto: server.dto.PaginatedShowInternalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.SumCostSales[]>>(`/show/internalenvironment/sum`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/internalenvironment/sum`,
        3145728
      ),
      PaginatedExternalEnvironment: endpoint(
        (dto: server.dto.ListShowExternalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ShowForListExternal[], server.dto.ListShowExternalFilters>>(`/show/externalenvironment/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/externalenvironment/list`,
        3145728
      ),
      GetLastModified: endpoint(
        (page: number, per_page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ShowLastModified[]>>(`/show/lastmodified`, { params: { page, per_page }, systemOneApiConfig }),
        (page: number, per_page: number) => `${baseURL}/show/lastmodified?page=${page}&per_page=${per_page}`,
        3145728
      ),
      GetMiniCalendarData: endpoint(
        (year: number, month: number, dto: server.dto.PaginatedShowInternalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.MiniCalendar>>(`/show/minicalendar/${year}/${month}`, dto, { systemOneApiConfig }),
        (year: number, month: number) => `${baseURL}/show/minicalendar/${year}/${month}`,
        3145728
      ),
      UpdateTags: endpoint(
        (dto: server.dto.UpdateParentTagsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/updateTags`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/updateTags`,
        3145728
      ),
      InsertForInternalEnvironment: endpoint(
        (show: server.dto.InsertShowInternalEnvironmentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/show/internalenvironment/insert`, show, { systemOneApiConfig }),
        () => `${baseURL}/show/internalenvironment/insert`,
        3145728
      ),
      InsertForExternalEnvironment: endpoint(
        (show: server.dto.InsertShowExternalEnvironmentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/show/externalenvironment/insert`, show, { systemOneApiConfig }),
        () => `${baseURL}/show/externalenvironment/insert`,
        3145728
      ),
      UpdateForInternalEnvironment: endpoint(
        (show: server.dto.UpdateShowInternalEnvironmentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/internalenvironment/update`, show, { systemOneApiConfig }),
        () => `${baseURL}/show/internalenvironment/update`,
        3145728
      ),
      UpdateFreeField: endpoint(
        (dto: server.dto.UpdateFreeFieldValueInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/updateFreeField`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/updateFreeField`,
        3145728
      ),
      UpdateForExternalEnvironment: endpoint(
        (show: server.dto.UpdateShowExternalEnvironmentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/externalenvironment/update`, show, { systemOneApiConfig }),
        () => `${baseURL}/show/externalenvironment/update`,
        3145728
      ),
      CopyShow: endpoint(
        (show: server.dto.CopyShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.CopyShowOutcome>>(`/show/copy`, show, { systemOneApiConfig }),
        () => `${baseURL}/show/copy`,
        3145728
      ),
      Delete: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/show/${showId}`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/show/${showId}`,
        3145728
      ),
      Approve: endpoint(
        (dto: server.dto.ApproveShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/ApproveShow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/ApproveShow`,
        3145728
      ),
      CountUnlockedShows: endpoint(
        (dto: server.dto.BatchLockShowsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BatchLockShowsOutcome>>(`/show/BatchLock/CountUnlocked`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/BatchLock/CountUnlocked`,
        3145728
      ),
      BatchLockShows: endpoint(
        (dto: server.dto.BatchLockShowsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.BatchLockShowsOutcome>>(`/show/BatchLock/Lock`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/BatchLock/Lock`,
        3145728
      ),
      GetProcessedEmailTemplate: endpoint(
        (dto: server.dto.GetProcessedEmailTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ProcessedEmailTemplate>>(`/show/GetProcessedEmailTemplate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/GetProcessedEmailTemplate`,
        3145728
      ),
      PaginatedFiles: endpoint(
        (dto: server.dto.PaginatedFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.File[]>>(`/show/listFiles`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/listFiles`,
        3145728
      ),
      PaginatedEmails: endpoint(
        (dto: server.dto.PaginatedEmailInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.EmailMessage[]>>(`/show/listEmails`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/listEmails`,
        3145728
      ),
      ListRelatedFiles: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>(`/show/${showId}/files/related`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/show/${showId}/files/related`,
        3145728
      ),
      SetExclusivityClause: endpoint(
        (dto: server.dto.SetExclusivityClauseInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/conflict/SetExclusivityClause`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/conflict/SetExclusivityClause`,
        3145728
      ),
      DeleteExclusivityClause: endpoint(
        (dto: server.dto.DeleteExclusivityClauseInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/conflict/DeleteExclusivityClause`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/conflict/DeleteExclusivityClause`,
        3145728
      ),
      CountConflicts: endpoint(
        (dto: server.dto.ListConflictsWithShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ConflictsCount>>(`/show/conflict/CountConflicts`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/conflict/CountConflicts`,
        3145728
      ),
      ListConflicts: endpoint(
        (dto: server.dto.ListConflictsWithShowInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.Conflicts>>(`/show/conflict/ListConflicts`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/conflict/ListConflicts`,
        3145728
      ),
      ExportList: endpoint(
        (fileName: string, page: number, templateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/show/export/list/${fileName}`, { params: { page, templateId }, systemOneApiConfig }),
        (fileName: string, page: number, templateId: number) => `${baseURL}/show/export/list/${fileName}?page=${page}&templateId=${templateId}`,
        3145728
      ),
      ExportGenericListForExternal: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/show/export/externalenvironment/list/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/show/export/externalenvironment/list/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomShowListForHelderWerk: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/show/export/list/custom/helderwerk/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/show/export/list/custom/helderwerk/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomShowListForPlatinum: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/show/export/list/custom/platinum/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/show/export/list/custom/platinum/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomShowListForTuiCruises: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/show/export/list/custom/tuicruises/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/show/export/list/custom/tuicruises/${fileName}?page=${page}`,
        3145728
      ),
      ExportCustomShowListForTemporarySecretary: endpoint(
        (fileName: string, tsExportType: server.dto.TemporarySecretaryExportTypes, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/show/export/list/custom/temporarysecretary/${fileName}`, { params: { tsExportType, page }, systemOneApiConfig }),
        (fileName: string, tsExportType: server.dto.TemporarySecretaryExportTypes, page: number) => `${baseURL}/show/export/list/custom/temporarysecretary/${fileName}?tsExportType=${tsExportType}&page=${page}`,
        3145728
      ),
      ExportCustomShowListForVanaf2: endpoint(
        (fileName: string, page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/show/export/list/custom/vanaf2/${fileName}`, { params: { page }, systemOneApiConfig }),
        (fileName: string, page: number) => `${baseURL}/show/export/list/custom/vanaf2/${fileName}?page=${page}`,
        3145728
      ),
      ExportItem: endpoint(
        (showId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/show/export/item/${showId}/${exportAction}/${clientFileName}`, { params: { wordTemplateId, preferredCulture }, systemOneApiConfig }),
        (showId: number, exportAction: server.dto.ExportActions, clientFileName: string, wordTemplateId: number, preferredCulture: server.dto.LCID) => `${baseURL}/show/export/item/${showId}/${exportAction}/${clientFileName}?wordTemplateId=${wordTemplateId}&preferredCulture=${preferredCulture}`,
        3145728
      ),
      ExportOptionsForListInternalEnvironment: endpoint(
        (dto: server.dto.PaginatedShowInternalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/show/internalenvironment/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/internalenvironment/ExportOptionsForList`,
        3145728
      ),
      ExportOptionsForListExternalEnvironment: endpoint(
        (dto: server.dto.ListShowExternalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/show/externalenvironment/ExportOptionsForList`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/externalenvironment/ExportOptionsForList`,
        3145728
      ),
      ExportOptionsForDetail: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ExportOption[]>>(`/show/ExportOptionsForDetail/${showId}`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/show/ExportOptionsForDetail/${showId}`,
        3145728
      ),
      Lock: endpoint(
        (dto: server.dto.LockUnlockShowInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/lock`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/lock`,
        3145728
      ),
      Unlock: endpoint(
        (dto: server.dto.LockUnlockShowInstuction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/unlock`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/unlock`,
        3145728
      ),
      GetLinkedTravel: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.ShowToTravelAppointment[]>>(`/show/${showId}/linkedtravel`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/show/${showId}/linkedtravel`,
        3145728
      ),
      GetTotals: endpoint(
        (dto: server.dto.ListShowTotalInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.GroupedShowTotals[], server.dto.ListShowTotalFilters>>(`/show/totals`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/totals`,
        3145728
      ),
      LinkFlight: endpoint(
        (dto: server.dto.LinkShowToItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/flight/link`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/flight/link`,
        3145728
      ),
      UnlinkFlight: endpoint(
        (dto: server.dto.UnlinkShowToItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/flight/unlink`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/flight/unlink`,
        3145728
      ),
      LinkTrain: endpoint(
        (dto: server.dto.LinkShowToItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/train/link`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/train/link`,
        3145728
      ),
      UnlinkTrainSegment: endpoint(
        (dto: server.dto.UnlinkShowToItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/train/unlink`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/train/unlink`,
        3145728
      ),
      LinkGroundTransport: endpoint(
        (dto: server.dto.LinkShowToItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/groundTransport/link`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/groundTransport/link`,
        3145728
      ),
      UnlinkGroundTransport: endpoint(
        (dto: server.dto.UnlinkShowToItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/show/groundTransport/unlink`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/groundTransport/unlink`,
        3145728
      ),
      ListShowsWithProducts: endpoint(
        (dto: server.dto.ListProductsByShowIdsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ShowWithProducts[]>>(`/show/showswithproducts`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/showswithproducts`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ShowActivity[]>>(`/show/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/show/activity`,
        3145728
      )
    },
    StateAlias: {
      ListUnmapped: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.StateAlias[]>>(`/statealias/unmapped`, { systemOneApiConfig }),
        () => `${baseURL}/statealias/unmapped`,
        3145728
      ),
      SetStateId: endpoint(
        (command: server.dto.SetStateAliasMappingInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/statealias/setstateid`, command, { systemOneApiConfig }),
        () => `${baseURL}/statealias/setstateid`,
        3145728
      ),
      SetCannotMap: endpoint(
        (command: server.dto.SetCannotMapStateAliasInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/statealias/setcannotmap`, command, { systemOneApiConfig }),
        () => `${baseURL}/statealias/setcannotmap`,
        3145728
      )
    },
    Status: {
      ListSmall: endpoint(
        (dto: server.dto.ListStatusInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.StatusSmall[]>>(`/status/list/small`, dto, { systemOneApiConfig }),
        () => `${baseURL}/status/list/small`,
        3145728
      ),
      List: endpoint(
        (dto: server.dto.ListStatusInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.Status[]>>(`/status/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/status/list`,
        3145728
      ),
      Paginated: endpoint(
        (dto: server.dto.PaginatedStatusInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.Status[], server.dto.ListStatusFilters>>(`/status/list/paginated`, dto, { systemOneApiConfig }),
        () => `${baseURL}/status/list/paginated`,
        3145728
      ),
      Create: endpoint(
        (instruction: server.dto.CreateStatusInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/status/create`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/status/create`,
        3145728
      ),
      Update: endpoint(
        (instruction: server.dto.UpdateStatusInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/status/update`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/status/update`,
        3145728
      ),
      Copy: endpoint(
        (statusId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/status/copy`, undefined, { params: { statusId }, systemOneApiConfig }),
        (statusId: number) => `${baseURL}/status/copy?statusId=${statusId}`,
        3145728
      ),
      DeleteAndMove: endpoint(
        (statusId: number, targetId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/status/deleteAndMove`, { params: { statusId, targetId }, systemOneApiConfig }),
        (statusId: number, targetId: number) => `${baseURL}/status/deleteAndMove?statusId=${statusId}&targetId=${targetId}`,
        3145728
      ),
      StoreSorted: endpoint(
        (dto: server.dto.StoreSortedStatusInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/status/sort`, dto, { systemOneApiConfig }),
        () => `${baseURL}/status/sort`,
        3145728
      )
    },
    Stripe: {
      PaymentIntentRetry: endpoint(
        (paymentIntentId: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/stripe/paymentintent/${paymentIntentId}/retry`, { systemOneApiConfig }),
        (paymentIntentId: string) => `${baseURL}/stripe/paymentintent/${paymentIntentId}/retry`,
        3145728
      ),
      GetCustomer: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.StripeCustomer>(`/stripe/${companyId}`, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/stripe/${companyId}`,
        3145728
      ),
      SyncCustomers: endpoint(
        (take: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/stripe/customers/sync`, undefined, { params: { take }, systemOneApiConfig }),
        (take: number) => `${baseURL}/stripe/customers/sync?take=${take}`,
        3145728
      ),
      CreateSetupIntent: endpoint(
        (dto: server.dto.CreateStripeSetupIntentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<string>(`/stripe/setupintent/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/stripe/setupintent/create`,
        3145728
      ),
      ListStripePaymentProblem: endpoint(
        (dto: server.dto.ListStripePaymentIntentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.StripePaymentIntent[]>>(`/stripe/payment/problem/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/stripe/payment/problem/list`,
        3145728
      ),
      CancelPaymentIntent: endpoint(
        (dto: server.dto.CancelStripePaymentIntentInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<string>(`/stripe/paymentIntent/cancel`, dto, { systemOneApiConfig }),
        () => `${baseURL}/stripe/paymentIntent/cancel`,
        3145728
      )
    },
    Subscription: {
      CheckMaximumAccountsReached: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.MaximumAccountsReachedResult>>(`/subscription/CheckMaximumAccountsReached`, { systemOneApiConfig }),
        () => `${baseURL}/subscription/CheckMaximumAccountsReached`,
        3145728
      ),
      SetClientPaymentMethodToInvoice: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/subscription/paymentmethod/invoice/set`, { systemOneApiConfig }),
        () => `${baseURL}/subscription/paymentmethod/invoice/set`,
        3145728
      )
    },
    Tag: {
      ListTags: endpoint(
        (mainArea: server.dto.MainAreas, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.TagSmall[]>>(`/tag/list/small`, { params: { mainArea }, systemOneApiConfig }),
        (mainArea: server.dto.MainAreas) => `${baseURL}/tag/list/small?mainArea=${mainArea}`,
        3145728
      ),
      Paginated: endpoint(
        (dto: server.dto.PaginatedTagInstruction, mainArea: server.dto.MainAreas, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.TagForList[], server.dto.ListTagFilters>>(`/tag/list`, dto, { params: { mainArea }, systemOneApiConfig }),
        (mainArea: server.dto.MainAreas) => `${baseURL}/tag/list?mainArea=${mainArea}`,
        3145728
      ),
      Create: endpoint(
        (instruction: server.dto.CreateTagInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/tag/create`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/tag/create`,
        3145728
      ),
      Update: endpoint(
        (instruction: server.dto.UpdateTagInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/tag/update`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/tag/update`,
        3145728
      ),
      Copy: endpoint(
        (tagId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/tag/copy`, undefined, { params: { tagId }, systemOneApiConfig }),
        (tagId: number) => `${baseURL}/tag/copy?tagId=${tagId}`,
        3145728
      ),
      Delete: endpoint(
        (tagId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/tag/delete`, { params: { tagId }, systemOneApiConfig }),
        (tagId: number) => `${baseURL}/tag/delete?tagId=${tagId}`,
        3145728
      ),
      DeleteAndMove: endpoint(
        (tagId: number, targetId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/tag/deleteAndMove`, { params: { tagId, targetId }, systemOneApiConfig }),
        (tagId: number, targetId: number) => `${baseURL}/tag/deleteAndMove?tagId=${tagId}&targetId=${targetId}`,
        3145728
      )
    },
    TaskManager: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedUserTaskInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.UserTask[], server.dto.ListUserTaskFilters>>(`/taskmanager/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/taskmanager/list`,
        3145728
      )
    },
    Timetable: {
      ListTimetableEntries: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.TimetableEntry[]>>(`/timetable/${showId}/timetable`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/timetable/${showId}/timetable`,
        3145728
      ),
      Duplicate: endpoint(
        (dto: server.dto.DuplicateTimetableInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/timetable/duplicate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/timetable/duplicate`,
        3145728
      ),
      CreateTimetableEntry: endpoint(
        (dto: server.dto.CreateTimetableEntryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapper<number>>(`/timetable/entry/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/timetable/entry/create`,
        3145728
      ),
      UpdateEntry: endpoint(
        (dto: server.dto.ModifyTimetableEntryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/timetable/entry/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/timetable/entry/update`,
        3145728
      ),
      DeleteEntry: endpoint(
        (dto: server.dto.DeleteTimetableEntryInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/timetable/entry/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/timetable/entry/delete`,
        3145728
      )
    },
    Tourdarts: {
      ListVenue: endpoint(
        (secret: string, dto: server.dto.PaginatedVenueInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.VenueForList[], server.dto.ListVenueFilters>>(`/tourdarts/ListVenue`, dto, { params: { secret }, systemOneApiConfig }),
        (secret: string) => `${baseURL}/tourdarts/ListVenue?secret=${secret}`,
        3145728
      ),
      ListPromoter: endpoint(
        (secret: string, dto: server.dto.PaginatedPromoterInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.PromoterForList[], server.dto.ListPromoterFilters>>(`/tourdarts/ListPromoter`, dto, { params: { secret }, systemOneApiConfig }),
        (secret: string) => `${baseURL}/tourdarts/ListPromoter?secret=${secret}`,
        3145728
      ),
      ListContactByCompany: endpoint(
        (secret: string, dto: server.dto.ListContactByCompanyInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.ContactWithWorkRelation[]>>(`/tourdarts/ListContactByCompany`, dto, { params: { secret }, systemOneApiConfig }),
        (secret: string) => `${baseURL}/tourdarts/ListContactByCompany?secret=${secret}`,
        3145728
      )
    },
    Train: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedTrainInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.TrainForList[], server.dto.ListTrainFilters>>(`/train/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/train/list`,
        3145728
      ),
      GetSidePanelData: endpoint(
        (trainId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.TrainSidePanelData>>(`/train/${trainId}/SidePanel`, { systemOneApiConfig }),
        (trainId: number) => `${baseURL}/train/${trainId}/SidePanel`,
        3145728
      ),
      CreateTrain: endpoint(
        (dto: server.dto.CreateTrainInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/train/createTrain`, dto, { systemOneApiConfig }),
        () => `${baseURL}/train/createTrain`,
        3145728
      ),
      UpdateTrain: endpoint(
        (dto: server.dto.UpdateTrainInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/train/updateTrain`, dto, { systemOneApiConfig }),
        () => `${baseURL}/train/updateTrain`,
        3145728
      ),
      CopyTrain: endpoint(
        (dto: server.dto.CopyTrainInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/train/copyTrain`, dto, { systemOneApiConfig }),
        () => `${baseURL}/train/copyTrain`,
        3145728
      ),
      DeleteTrain: endpoint(
        (dto: server.dto.DeleteTrainInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/train/deleteTrain`, dto, { systemOneApiConfig }),
        () => `${baseURL}/train/deleteTrain`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.TrainActivity[]>>(`/train/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/train/activity`,
        3145728
      ),
      UploadFiles: endpoint(
        (dto: server.dto.UploadMultipleTrainFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/train/uploadFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/train/uploadFile`,
        10485760
      ),
      DownloadFile: endpoint(
        (fileId: string, fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/train/${fileId}/download/${fileName}`, { systemOneApiConfig }),
        (fileId: string, fileName: string) => `${baseURL}/train/${fileId}/download/${fileName}`,
        3145728
      ),
      DeleteFile: endpoint(
        (dto: server.dto.DeleteTrainFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/train/deleteFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/train/deleteFile`,
        3145728
      ),
      UpdateFileName: endpoint(
        (dto: server.dto.UpdateTrainFileNameInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/train/updateFileName`, dto, { systemOneApiConfig }),
        () => `${baseURL}/train/updateFileName`,
        3145728
      )
    },
    Url: {
      Reformat: endpoint(
        (value: string, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/url/list`, undefined, { params: { value }, systemOneApiConfig }),
        (value: string) => `${baseURL}/url/list?value=${value}`,
        3145728
      )
    },
    UserAction: {
      List: endpoint(
        (dto: server.dto.PaginatedUserActionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.UserAction[], server.dto.ListUserActionFilters>>(`/useraction/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/useraction/list`,
        3145728
      )
    },
    User: {
      SetAvatar: endpoint(
        (instruction: server.dto.SetUserAvatarInstruction, userId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/user/${userId}/avatar`, instruction, { systemOneApiConfig }),
        (userId: number) => `${baseURL}/user/${userId}/avatar`,
        10485760
      ),
      ResetPassword: endpoint(
        (userId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<string>>(`/user/${userId}/resetpassword`, { systemOneApiConfig }),
        (userId: number) => `${baseURL}/user/${userId}/resetpassword`,
        3145728
      ),
      SetHubSpotContactId: endpoint(
        (dto: server.dto.SetHubSpotContactIdInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/user/update/HubSpotContactId`, dto, { systemOneApiConfig }),
        () => `${baseURL}/user/update/HubSpotContactId`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.UserActivity[]>>(`/user/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/user/activity`,
        3145728
      )
    },
    UserEmailProfile: {
      GetUserEmailProfile: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserEmailProfile>>(`/useremailprofile`, { systemOneApiConfig }),
        () => `${baseURL}/useremailprofile`,
        3145728
      ),
      UpdateEmailPreferences: endpoint(
        (dto: server.dto.UpdateUserEmailProfileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/useremailprofile/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/useremailprofile/update`,
        3145728
      )
    },
    UserForAdminOne: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedUserForAdminOneInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.UserForAdminOne[], server.dto.ListUserForAdminOneFilters>>(`/userforadminone/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforadminone/list`,
        3145728
      )
    },
    UserForExternalAdmin: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedExternalUserInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.UserForExternalAdminForList[], server.dto.ListExternalUserFilters>>(`/userforexternaladmin/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforexternaladmin/list`,
        3145728
      ),
      GetUserToClientForExternal: endpoint(
        (userToClientId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserForExternalAdmin>>(`/userforexternaladmin/${userToClientId}`, { systemOneApiConfig }),
        (userToClientId: number) => `${baseURL}/userforexternaladmin/${userToClientId}`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforexternaladmin/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforexternaladmin/update`,
        3145728
      ),
      UpdatePermissions: endpoint(
        (dto: server.dto.UpdateExternalEnvironmentPermissionsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforexternaladmin/updatePermissions`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforexternaladmin/updatePermissions`,
        3145728
      ),
      Invite: endpoint(
        (dto: server.dto.InviteExternalUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/userforexternaladmin/invite`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforexternaladmin/invite`,
        3145728
      ),
      Reactivate: endpoint(
        (dto: server.dto.ReactivateExternalUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforexternaladmin/reactivate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforexternaladmin/reactivate`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.UserToClientActivity[]>>(`/userforexternaladmin/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforexternaladmin/activity`,
        3145728
      )
    },
    UserForInternalAdmin: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedInternalUserInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.UserForInternalAdminForList[], server.dto.ListInternalUserFilters>>(`/userforinternaladmin/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/list`,
        3145728
      ),
      GetUserToClientForInternal: endpoint(
        (userToClientId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserForInternalAdmin>>(`/userforinternaladmin/${userToClientId}`, { systemOneApiConfig }),
        (userToClientId: number) => `${baseURL}/userforinternaladmin/${userToClientId}`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforinternaladmin/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/update`,
        3145728
      ),
      UpdatePermissions: endpoint(
        (dto: server.dto.UpdateInternalEnvironmentPermissionsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforinternaladmin/updatePermissions`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/updatePermissions`,
        3145728
      ),
      Invite: endpoint(
        (dto: server.dto.InviteInternalUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/userforinternaladmin/invite`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/invite`,
        3145728
      ),
      Reactivate: endpoint(
        (dto: server.dto.ReactivateInternalUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforinternaladmin/reactivate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/reactivate`,
        3145728
      ),
      SetAccessAllArtists: endpoint(
        (dto: server.dto.SetUserForInternalAccessAllArtistsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforinternaladmin/SetAccessAllArtists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/SetAccessAllArtists`,
        3145728
      ),
      PaginatedLinkedBookkeepings: endpoint(
        (dto: server.dto.PaginatedUserToClientToBookkeepingLinkedBookkeepingsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.BookkeepingSmall[], server.dto.ListLinkedBookkeepingFilters>>(`/userforinternaladmin/paginatedLinkedBookkeepings`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/paginatedLinkedBookkeepings`,
        3145728
      ),
      LinkBookkeepings: endpoint(
        (dto: server.dto.UserToClientToBookkeepingLinkUnlinkBookkeepingsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforinternaladmin/linkBookkeepings`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/linkBookkeepings`,
        3145728
      ),
      UnlinkBookkeepings: endpoint(
        (dto: server.dto.UserToClientToBookkeepingLinkUnlinkBookkeepingsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforinternaladmin/unlinkBookkeepings`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/unlinkBookkeepings`,
        3145728
      ),
      LinkAllForUserToClient: endpoint(
        (dto: server.dto.UserToClientToBookkeepingLinkUnlinkAllForUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforinternaladmin/linkAllForUserToClient`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/linkAllForUserToClient`,
        3145728
      ),
      UnlinkAllForUserToClient: endpoint(
        (dto: server.dto.UserToClientToBookkeepingLinkUnlinkAllForUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userforinternaladmin/unlinkAllForUserToClient`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/unlinkAllForUserToClient`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.UserToClientActivity[]>>(`/userforinternaladmin/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userforinternaladmin/activity`,
        3145728
      )
    },
    UserPreference: {
      GetUserPreference: endpoint(
        (userId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserPreference>>(`/userpreference/${userId}`, { systemOneApiConfig }),
        (userId: number) => `${baseURL}/userpreference/${userId}`,
        3145728
      ),
      UpdateDefaults: endpoint(
        (dto: server.dto.UpdateUserDefaultsPreferenceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userpreference/defaults`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userpreference/defaults`,
        3145728
      ),
      UpdateDisplay: endpoint(
        (dto: server.dto.UpdateUserDisplayPreferenceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userpreference/display`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userpreference/display`,
        3145728
      ),
      UpdateRegional: endpoint(
        (dto: server.dto.UpdateUserRegionalPreferenceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userpreference/regional`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userpreference/regional`,
        3145728
      ),
      UpdateStyling: endpoint(
        (dto: server.dto.UpdateUserStylingPreferenceInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/userpreference/styling`, dto, { systemOneApiConfig }),
        () => `${baseURL}/userpreference/styling`,
        3145728
      )
    },
    UserSignature: {
      GetUserSignature: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserSignature>>(`/usersignature/get`, { systemOneApiConfig }),
        () => `${baseURL}/usersignature/get`,
        3145728
      ),
      SetUserSignature: endpoint(
        (dto: server.dto.SetUserSignatureInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usersignature/set`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usersignature/set`,
        3145728
      ),
      DeleteUserSignature: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/usersignature/delete`, { systemOneApiConfig }),
        () => `${baseURL}/usersignature/delete`,
        3145728
      ),
      UploadSignatureFile: endpoint(
        (instruction: server.dto.UploadSignatureInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/usersignature/upload`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/usersignature/upload`,
        10485760
      ),
      DownloadSignatureFile: endpoint(
        (fileId: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/usersignature/download/signature.png`, { params: { fileId }, systemOneApiConfig }),
        (fileId: string) => `${baseURL}/usersignature/download/signature.png?fileId=${fileId}`,
        3145728
      )
    },
    UserTask: {
      List: endpoint(
        (instruction: server.dto.ListUserTaskPerItemInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.UserTask[]>>(`/usertask/list`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/usertask/list`,
        3145728
      ),
      CreateUserTask: endpoint(
        (instruction: server.dto.CreateUserTaskInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapper<number>>(`/usertask/create`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/usertask/create`,
        3145728
      ),
      UpdateUserTask: endpoint(
        (instruction: server.dto.UpdateUserTaskInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usertask/update`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/usertask/update`,
        3145728
      ),
      DeleteUserTask: endpoint(
        (userTaskId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/usertask/${userTaskId}`, { systemOneApiConfig }),
        (userTaskId: number) => `${baseURL}/usertask/${userTaskId}`,
        3145728
      ),
      ConvertToNote: endpoint(
        (dto: server.dto.ConvertUserTaskToNoteInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.Note>>(`/usertask/converttonote`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertask/converttonote`,
        3145728
      ),
      SetTaskReminderEmail: endpoint(
        (dto: server.dto.SetUserTaskEmailReminderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<boolean>>(`/usertask/reminderemail/set`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertask/reminderemail/set`,
        3145728
      ),
      SetTaskReminderPopup: endpoint(
        (dto: server.dto.SetUserTaskPopupReminderInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<boolean>>(`/usertask/reminderpopup/set`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertask/reminderpopup/set`,
        3145728
      ),
      ReminderHealth: endpoint(
        (type: server.dto.UserTaskReminderTypes, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserTaskReminderHealth>>(`/usertask/reminder/${type}/health`, { systemOneApiConfig }),
        (type: server.dto.UserTaskReminderTypes) => `${baseURL}/usertask/reminder/${type}/health`,
        3145728
      ),
      ByUser: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.TasksByUser[]>>(`/usertask/byuser`, { systemOneApiConfig }),
        () => `${baseURL}/usertask/byuser`,
        3145728
      )
    },
    UserTaskTemplate: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedUserTaskTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.UserTaskTemplate[], server.dto.ListUserTaskTemplateFilters>>(`/usertasktemplate/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertasktemplate/list`,
        3145728
      ),
      CreateUserTaskTemplate: endpoint(
        (dto: server.dto.CreateUserTaskTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/usertasktemplate/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertasktemplate/create`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateUserTaskTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usertasktemplate/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertasktemplate/update`,
        3145728
      ),
      Copy: endpoint(
        (dto: server.dto.CopyUserTaskTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/usertasktemplate/copy`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertasktemplate/copy`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteUserTaskTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usertasktemplate/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertasktemplate/delete`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.UserTaskTemplateActivity[]>>(`/usertasktemplate/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertasktemplate/activity`,
        3145728
      )
    },
    UserToClient: {
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.UserToClientSmall[], server.dto.ListUserToClientFilters>>(`/usertoclient/listSmall`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertoclient/listSmall`,
        3145728
      ),
      GetUserToClientUsage: endpoint(
        (userToClientId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.UserToClientUsage>>(`/usertoclient/${userToClientId}/usage`, { systemOneApiConfig }),
        (userToClientId: number) => `${baseURL}/usertoclient/${userToClientId}/usage`,
        3145728
      ),
      TransferUserToClientUsage: endpoint(
        (dto: server.dto.TransferUserToClientUsageInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usertoclient/usage/transfer`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertoclient/usage/transfer`,
        3145728
      ),
      SetUserToClientFreeFieldProfile: endpoint(
        (dto: server.dto.UserToClientsSetFreeFieldProfileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usertoclient/setUserToClientsFreeFieldProfile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertoclient/setUserToClientsFreeFieldProfile`,
        3145728
      ),
      StoreBookkeepingsToUserToClient: endpoint(
        (dto: server.dto.StoreBookkeepingsToUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usertoclient/bookkeepings/store`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertoclient/bookkeepings/store`,
        3145728
      ),
      Deactivate: endpoint(
        (dto: server.dto.DeactivateUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usertoclient/deactivate`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertoclient/deactivate`,
        3145728
      ),
      PaginatedAssociatedArtistsSmall: endpoint(
        (dto: server.dto.PaginatedLinkedArtistToUserToClientInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ArtistSmall[], server.dtofilters.ListLinkedArtistFilters>>(`/usertoclient/paginatedLinkedArtists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertoclient/paginatedLinkedArtists`,
        3145728
      ),
      LinkArtists: endpoint(
        (dto: server.dto.UserToClientToArtistLinkUnlinkArtistsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usertoclient/linkArtists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertoclient/linkArtists`,
        3145728
      ),
      UnlinkArtists: endpoint(
        (dto: server.dto.UserToClientToArtistLinkUnlinkArtistsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/usertoclient/unlinkArtists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/usertoclient/unlinkArtists`,
        3145728
      )
    },
    VatRate: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedVatRateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.VatRate[], server.dto.ListVatRateFilters>>(`/vatrate/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/vatrate/list`,
        3145728
      )
    },
    Venue: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedVenueInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.VenueForList[], server.dto.ListVenueFilters>>(`/venue/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/venue/list`,
        3145728
      ),
      PaginatedSmall: endpoint(
        (dto: server.dto.PaginatedVenueInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.VenueSmall[], server.dto.ListVenueFilters>>(`/venue/listSmall`, dto, { systemOneApiConfig }),
        () => `${baseURL}/venue/listSmall`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.SetVenueDetailsInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/venue/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/venue/update`,
        3145728
      ),
      GetVenue: endpoint(
        (venueId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.Venue>>(`/venue/${venueId}`, { systemOneApiConfig }),
        (venueId: number) => `${baseURL}/venue/${venueId}`,
        3145728
      )
    },
    WebflowWebhook: {
      FormSubmission: endpoint(
        (dto: server.dto.WebflowFormSubmissionInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/webhook/webflow`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webhook/webflow`,
        3145728
      )
    },
    WebformConfiguration: {
      Create: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/webformconfiguration/create`, undefined, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/create`,
        3145728
      ),
      Paginated: endpoint(
        (dto: server.dto.PaginatedWebformInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.WebformForList[], server.dto.ListWebformFilters>>(`/webformconfiguration/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/list`,
        3145728
      ),
      Update: endpoint(
        (dto: server.dto.UpdateWebformInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/update`,
        3145728
      ),
      UpdateArtistFunctionGroup: endpoint(
        (dto: server.dto.UpdateWebformArtistFunctionGroupInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/updateArtistFunctionGroup`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/updateArtistFunctionGroup`,
        3145728
      ),
      UpdateVenueFunctionGroup: endpoint(
        (dto: server.dto.UpdateWebformVenueFunctionGroupInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/updateVenueFunctionGroup`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/updateVenueFunctionGroup`,
        3145728
      ),
      UpdatePromoterFunctionGroup: endpoint(
        (dto: server.dto.UpdateWebformPromoterFunctionGroupInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/updatePromoterFunctionGroup`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/updatePromoterFunctionGroup`,
        3145728
      ),
      Delete: endpoint(
        (dto: server.dto.DeleteWebformInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/delete`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/delete`,
        3145728
      ),
      PaginatedLinkedArtists: endpoint(
        (dto: server.dto.PaginatedLinkedArtistToWebformInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.ArtistSmall[], server.dtofilters.ListLinkedArtistFilters>>(`/webformconfiguration/paginatedLinkedArtists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/paginatedLinkedArtists`,
        3145728
      ),
      LinkArtists: endpoint(
        (dto: server.dto.LinkUnlinkArtistToWebformInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/linkArtists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/linkArtists`,
        3145728
      ),
      UnlinkArtists: endpoint(
        (dto: server.dto.LinkUnlinkArtistToWebformInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/unlinkArtists`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/unlinkArtists`,
        3145728
      ),
      LinkAllForWebform: endpoint(
        (webformId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/linkAllForWebform`, undefined, { params: { webformId }, systemOneApiConfig }),
        (webformId: number) => `${baseURL}/webformconfiguration/linkAllForWebform?webformId=${webformId}`,
        3145728
      ),
      UnlinkAllForWebform: endpoint(
        (webformId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/unlinkAllForWebform`, undefined, { params: { webformId }, systemOneApiConfig }),
        (webformId: number) => `${baseURL}/webformconfiguration/unlinkAllForWebform?webformId=${webformId}`,
        3145728
      ),
      DeleteFreefield: endpoint(
        (dto: server.dto.DeleteWebformFreefieldInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/deleteFreeField`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/deleteFreeField`,
        3145728
      ),
      StoreFreefield: endpoint(
        (dto: server.dto.StoreWebformFreefieldInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/storeFreeField`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/storeFreeField`,
        3145728
      ),
      SortFreefields: endpoint(
        (dto: server.dto.SortWebformFreefieldInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webformconfiguration/sortFreeFields`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/sortFreeFields`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.WebformActivity[]>>(`/webformconfiguration/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/webformconfiguration/activity`,
        3145728
      )
    },
    Webform: {
      CreateLog: endpoint(
        (instruction: server.dto.CreateWebformLogInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/webform/log`, instruction, { systemOneApiConfig }),
        () => `${baseURL}/webform/log`,
        3145728
      )
    },
    WordTemplate: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedWordTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.WordTemplate[], server.dto.ListWordTemplateFilters>>(`/wordtemplate/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/list`,
        3145728
      ),
      Copy: endpoint(
        (wordTemplateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/wordtemplate/copy`, undefined, { params: { wordTemplateId }, systemOneApiConfig }),
        (wordTemplateId: number) => `${baseURL}/wordtemplate/copy?wordTemplateId=${wordTemplateId}`,
        3145728
      ),
      Delete: endpoint(
        (wordTemplateId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/wordtemplate/delete`, undefined, { params: { wordTemplateId }, systemOneApiConfig }),
        (wordTemplateId: number) => `${baseURL}/wordtemplate/delete?wordTemplateId=${wordTemplateId}`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateWordTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/wordtemplate/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/create`,
        10485760
      ),
      Update: endpoint(
        (dto: server.dto.UpdateWordTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/wordtemplate/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/update`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.WordTemplateActivity[]>>(`/wordtemplate/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/activity`,
        3145728
      ),
      PaginatedLinkedBookkeepings: endpoint(
        (dto: server.dto.PaginatedLinkedBookkeepingsToWordTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.BookkeepingSmall[], server.dto.ListLinkedBookkeepingFilters>>(`/wordtemplate/paginatedLinkedBookkeepings`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/paginatedLinkedBookkeepings`,
        3145728
      ),
      LinkBookkeepings: endpoint(
        (dto: server.dto.LinkUnlinkBookkeepingToWordTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/wordtemplate/linkBookkeepings`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/linkBookkeepings`,
        3145728
      ),
      UnlinkBookkeepings: endpoint(
        (dto: server.dto.LinkUnlinkBookkeepingToWordTemplateInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/wordtemplate/unlinkBookkeepings`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/unlinkBookkeepings`,
        3145728
      ),
      LinkAllForWordTemplate: endpoint(
        (wordTemplateForDetailViewId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/wordtemplate/linkAllForWordTemplate`, undefined, { params: { wordTemplateForDetailViewId }, systemOneApiConfig }),
        (wordTemplateForDetailViewId: number) => `${baseURL}/wordtemplate/linkAllForWordTemplate?wordTemplateForDetailViewId=${wordTemplateForDetailViewId}`,
        3145728
      ),
      UnlinkAllForWordTemplate: endpoint(
        (wordTemplateForDetailViewId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/wordtemplate/unlinkAllForWordTemplate`, undefined, { params: { wordTemplateForDetailViewId }, systemOneApiConfig }),
        (wordTemplateForDetailViewId: number) => `${baseURL}/wordtemplate/unlinkAllForWordTemplate?wordTemplateForDetailViewId=${wordTemplateForDetailViewId}`,
        3145728
      ),
      DownloadFile: endpoint(
        (dto: server.dto.DownloadTemplateFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/wordtemplate/DownloadFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/DownloadFile`,
        3145728
      ),
      DownloadFileFromActivity: endpoint(
        (dto: server.dto.DownloadTemplateFileFromActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/wordtemplate/DownloadFileFromActivity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/DownloadFileFromActivity`,
        3145728
      ),
      UploadFile: endpoint(
        (dto: server.dto.UploadTemplateFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/wordtemplate/UploadFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplate/UploadFile`,
        10485760
      ),
      RestoreFileFromActivity: endpoint(
        (activityId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/wordtemplate/RestoreFileFromActivity`, undefined, { params: { activityId }, systemOneApiConfig }),
        (activityId: number) => `${baseURL}/wordtemplate/RestoreFileFromActivity?activityId=${activityId}`,
        3145728
      )
    },
    WordTemplateForListView: {
      Paginated: endpoint(
        (dto: server.dto.PaginatedWordTemplateForListViewInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<server.dto.WordTemplateForListView[], server.dto.ListWordTemplateForListViewFilters>>(`/wordtemplateforlistview/list`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplateforlistview/list`,
        3145728
      ),
      DeleteWordTemplateForListView: endpoint(
        (wordTemplateForListViewId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/wordtemplateforlistview/${wordTemplateForListViewId}`, { systemOneApiConfig }),
        (wordTemplateForListViewId: number) => `${baseURL}/wordtemplateforlistview/${wordTemplateForListViewId}`,
        3145728
      ),
      CopyWordTemplateForListView: endpoint(
        (wordTemplateForListViewId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<number>>(`/wordtemplateforlistview/${wordTemplateForListViewId}/copy`, undefined, { systemOneApiConfig }),
        (wordTemplateForListViewId: number) => `${baseURL}/wordtemplateforlistview/${wordTemplateForListViewId}/copy`,
        3145728
      ),
      Create: endpoint(
        (dto: server.dto.CreateWordTemplateForListViewInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/wordtemplateforlistview/create`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplateforlistview/create`,
        10485760
      ),
      Update: endpoint(
        (dto: server.dto.UpdateWordTemplateForListViewInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/wordtemplateforlistview/update`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplateforlistview/update`,
        3145728
      ),
      ListActivity: endpoint(
        (dto: server.dto.PaginatedItemActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.WordTemplateForListViewActivity[]>>(`/wordtemplateforlistview/activity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplateforlistview/activity`,
        3145728
      ),
      DownloadFile: endpoint(
        (dto: server.dto.DownloadTemplateFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/wordtemplateforlistview/DownloadFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplateforlistview/DownloadFile`,
        3145728
      ),
      DownloadFileFromActivity: endpoint(
        (dto: server.dto.DownloadTemplateFileFromActivityInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/wordtemplateforlistview/DownloadFileFromActivity`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplateforlistview/DownloadFileFromActivity`,
        3145728
      ),
      UploadFile: endpoint(
        (dto: server.dto.UploadTemplateFileInstruction, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.FileUploadResult>>(`/wordtemplateforlistview/UploadFile`, dto, { systemOneApiConfig }),
        () => `${baseURL}/wordtemplateforlistview/UploadFile`,
        10485760
      ),
      RestoreFileFromActivity: endpoint(
        (activityId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/wordtemplateforlistview/RestoreFileFromActivity`, undefined, { params: { activityId }, systemOneApiConfig }),
        (activityId: number) => `${baseURL}/wordtemplateforlistview/RestoreFileFromActivity?activityId=${activityId}`,
        3145728
      )
    },

  }

  return apiFunctions;
}

export { createApiFunctions, api };
